import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import {
  onAuthChange, apiLogin, apiRegister, apiSignOut, apiChangePassword, apiForgotPassword,
  getUserProfile, updateUserProfile, approveUser, getAllUsers,
  saveProfilePhoto, getProfilePhoto, saveUserTheme, updateLastActive,
  loadCollabs, saveCollab, deleteCollab, getCollabByShareId,
  loadInvoices, saveInvoice, deleteInvoice, getNextInvoiceNumber,
  loadCampaigns, saveCampaign, deleteCampaign,
  loadLinksPage, saveLinksConfig, saveLinkItem, deleteLinkItem,
  getLinksPhoto, getLinkItemPhoto,
  uid, generateShareId, ADMIN_EMAIL,
} from './storage';
import {
  STAGES, COLLAB_TYPES, PRIORITIES, CATEGORIES,
  DELIVERY_STATUS, SHOOT_LOCATIONS, DEFAULT_RATE_ITEMS,
  DELIVERABLE_TIPS, LINK_CATEGORIES,
} from './constants';
import { THEMES, HOW_TO_USE, applyTheme } from './themes';
import {
  Plus, LayoutGrid, List, X, ChevronRight, Instagram, Clock,
  DollarSign, Package, Camera, Edit3, Share2, ArrowLeft, Check,
  Sparkles, TrendingUp, AlertCircle, Zap, Timer, Save, Flag,
  Lock, Eye, EyeOff, Copy, ExternalLink, Settings, LogOut,
  User, Users, FileText, Lightbulb, Link, Receipt, ChevronDown,
  ChevronUp, Trash2, AlertTriangle, CheckCircle, UserCheck,
  Calendar, MapPin, Globe, HelpCircle, Palette, ChevronLeft,
  BookOpen, Star,
} from 'lucide-react';
import './App.css';

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function dueInfo(d) {
  if (!d) return null;
  const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, type: 'over' };
  if (diff === 0) return { label: 'Due today', type: 'warn' };
  if (diff <= 5) return { label: `${diff}d left`, type: 'warn' };
  return { label: `${diff}d left`, type: 'ok' };
}
function brandDeadlineInfo(c) {
  if (!c.brandDeadlineDays || !c.productReceivedDate) return null;
  const dl = new Date(c.productReceivedDate);
  dl.setDate(dl.getDate() + parseInt(c.brandDeadlineDays));
  const diff = Math.ceil((dl - new Date()) / 86400000);
  const dlStr = dl.toISOString().slice(0, 10);
  if (diff < 0) return { label: `${Math.abs(diff)}d past brand deadline`, type: 'over', deadline: dlStr };
  if (diff === 0) return { label: 'Brand deadline today!', type: 'over', deadline: dlStr };
  if (diff <= 3) return { label: `${diff}d to brand deadline`, type: 'over', deadline: dlStr };
  if (diff <= 7) return { label: `${diff}d to brand deadline`, type: 'warn', deadline: dlStr };
  return { label: `${diff}d to brand deadline`, type: 'ok', deadline: dlStr };
}
function stagePct(s) { return Math.round((s / (STAGES.length - 1)) * 100); }
function effPriority(c) {
  const bd = brandDeadlineInfo(c);
  if (bd?.type === 'over') return 'urgent';
  if (dueInfo(c.due)?.type === 'over') return 'urgent';
  return c.priority || 'normal';
}

// ─── ATOMS ────────────────────────────────────────────────────────────────────
function Badge({ bg, color, label, small }) {
  return <span className={`badge${small ? ' sm' : ''}`} style={{ background: bg, color }}>{label}</span>;
}
const StageBadge = ({ stage, small }) => { const s = STAGES[stage] || STAGES[0]; return <Badge bg={s.bg} color={s.color} label={s.label} small={small} />; };
const TypeBadge = ({ type, small }) => { const t = COLLAB_TYPES.find(x => x.id === type) || COLLAB_TYPES[0]; return <Badge bg={t.bg} color={t.color} label={t.label} small={small} />; };
const PriorityBadge = ({ priority, small }) => { const p = PRIORITIES.find(x => x.id === priority) || PRIORITIES[2]; return <Badge bg={p.bg} color={p.color} label={p.label} small={small} />; };
const DueBadge = ({ dateStr }) => { const d = dueInfo(dateStr); if (!d) return null; return <span className={`due-chip ${d.type}`}>{d.label}</span>; };
const BrandDlBadge = ({ collab }) => { const d = brandDeadlineInfo(collab); if (!d) return null; return <span className={`due-chip ${d.type}`} style={{ display:'inline-flex',alignItems:'center',gap:3 }}><Timer size={9}/>{d.label}</span>; };

function Avatar({ name, size = 36 }) {
  const initials = name ? name.slice(0, 2).toUpperCase() : '??';
  const palette = ['#C9A84C','#A8893A','#D4956A','#6EE7B7','#FCA5A5','#8B7355','#E8C97A'];
  const bg = palette[name ? name.charCodeAt(0) % palette.length : 0];
  return <div className="avatar" style={{ width: size, height: size, background: bg, fontSize: size * 0.34 }}>{initials}</div>;
}

function ProgressBar({ stage, collab, showLabel }) {
  const pct = stagePct(stage);
  const bd = brandDeadlineInfo(collab);
  const isUrgent = bd?.type === 'over' || dueInfo(collab?.due)?.type === 'over';
  const color = isUrgent ? '#EF4444' : pct === 100 ? '#059669' : pct >= 66 ? '#10B981' : pct >= 33 ? '#C9A84C' : '#A8893A';
  return (
    <div className="pbar-wrap">
      <div className="pbar-track"><div className="pbar-fill" style={{ width: `${pct}%`, background: color }} /></div>
      {showLabel && <span className="pbar-pct" style={{ color }}>{pct}%</span>}
    </div>
  );
}

function Field({ label, children, style }) {
  return <div className="form-field" style={style}><label>{label}</label>{children}</div>;
}
function Row({ l, children }) {
  return <div className="detail-row"><span>{l}</span>{children}</div>;
}


// ─── LANDING PAGE ─────────────────────────────────────────────────────────────
function LandingPage({ onLogin, onSignup }) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const features = [
    { icon: '🤝', title: 'Collab Pipeline', desc: 'Track every brand deal from first DM to payment. 11 stages, priority alerts, brand deadlines.' },
    { icon: '🧾', title: 'Pro Invoicing', desc: 'Generate polished PDF invoices in seconds. Single payments, milestones, auto-filled rates.' },
    { icon: '📅', title: 'Smart Calendar', desc: 'Shoots, due dates, go-lives — all your deadlines in one view. Mark done in one tap.' },
    { icon: '🔗', title: 'Links Page', desc: 'Your personal brand code page. Share coupons, affiliate links and promos with followers.' },
    { icon: '📊', title: 'Dashboard', desc: 'Monthly earnings, pending income, active collabs — your business at a glance every login.' },
    { icon: '📤', title: 'Brand Sharing', desc: 'Send brands a live progress link — no login needed. They see stages, deliverables, shoot details.' },
  ];

  return (
    <div className="landing">
      {/* ── NAV ── */}
      <nav className={`landing-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="landing-nav-inner">
          <div className="landing-logo">
            <svg width="28" height="28" viewBox="0 0 44 44" fill="none">
              <rect width="44" height="44" rx="10" fill="url(#lg)"/>
              <defs><linearGradient id="lg" x1="0" y1="0" x2="44" y2="44" gradientUnits="userSpaceOnUse"><stop stopColor="#C9A84C"/><stop offset="1" stopColor="#8B7355"/></linearGradient></defs>
              <g transform="translate(22,22)" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="-12,1 -12,13 12,13 12,1" fill="rgba(255,255,255,0.2)"/>
                <polyline points="-12,1 -5,-4 5,-4 12,1"/>
                <line x1="0" y1="-4" x2="0" y2="1"/>
                <rect x="-10" y="-12" width="8" height="6" rx="1.5" fill="rgba(255,255,255,0.15)"/>
                <circle cx="-6" cy="-9" r="1.8" fill="rgba(255,255,255,0.2)"/>
                <line x1="0" y1="-13" x2="0" y2="-4"/>
                <polygon points="0,-16 -2,-13 2,-13" fill="white" strokeWidth="0.5"/>
              </g>
            </svg>
            <span>My Creator Kit</span>
          </div>
          <div className="landing-nav-links">
            <a href="#features" className="landing-nav-link">Features</a>
            <a href="#how" className="landing-nav-link">How it works</a>
            <button className="landing-nav-btn-ghost" onClick={onLogin}>Log in</button>
            <button className="landing-nav-btn" onClick={onSignup}>Get started</button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="landing-hero">
        <div className="landing-hero-bg">
          <div className="landing-orb landing-orb1"/>
          <div className="landing-orb landing-orb2"/>
          <div className="landing-orb landing-orb3"/>
          {/* Subtle grain texture */}
          <div className="landing-grain"/>
        </div>
        <div className="landing-hero-content">
          <div className="landing-eyebrow">✦ The creator business OS</div>
          <h1 className="landing-h1">
            Run your collab<br/>
            <em>business like a pro</em>
          </h1>
          <p className="landing-hero-sub">
            Track brand deals, generate invoices, manage deadlines and share 
            progress — all in one place built for content creators.
          </p>
          <div className="landing-hero-cta">
            <button className="landing-cta-primary" onClick={onSignup}>
              Start for free
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
            <button className="landing-cta-ghost" onClick={onLogin}>
              Already a member? Log in
            </button>
          </div>
          <div className="landing-hero-proof">
            <div className="landing-proof-item"><span className="landing-proof-num">500K+</span><span>Monthly reach tracked</span></div>
            <div className="landing-proof-div"/>
            <div className="landing-proof-item"><span className="landing-proof-num">11</span><span>Collab stages</span></div>
            <div className="landing-proof-div"/>
            <div className="landing-proof-item"><span className="landing-proof-num">∞</span><span>Brands, invoices, links</span></div>
          </div>
        </div>

        {/* ── HERO IMAGE / MOCKUP ── */}
        <div className="landing-hero-visual">
          <div className="landing-mockup">
            <div className="landing-mockup-bar">
              <div className="landing-mockup-dots">
                <span/><span/><span/>
              </div>
              <div className="landing-mockup-url">mycreatorkit.app</div>
            </div>
            <div className="landing-mockup-body">
              {/* Mini dashboard preview */}
              <div className="lm-greeting">Good morning, James xx ✦</div>
              <div className="lm-stats">
                <div className="lm-stat"><div className="lm-stat-label">Earned this Month</div><div className="lm-stat-val">$4,200</div><div className="lm-stat-up">↑ 28%</div></div>
                <div className="lm-stat"><div className="lm-stat-label">Pending</div><div className="lm-stat-val">$1,800</div><div className="lm-stat-neu">2 invoices</div></div>
                <div className="lm-stat"><div className="lm-stat-label">Active</div><div className="lm-stat-val">6</div><div className="lm-stat-neu">collabs</div></div>
              </div>
              <div className="lm-section">Upcoming this week</div>
              {[
                { brand:'Melbourne Visit', label:'Content due', color:'#C9A84C', day:'Today' },
                { brand:'Coles', label:'Shoot', color:'#EC4899', day:'Tomorrow' },
                { brand:'Tim Tam', label:'Go-live', color:'#10B981', day:'In 3d' },
              ].map((e,i) => (
                <div key={i} className="lm-event">
                  <div className="lm-event-dot" style={{background:e.color}}/>
                  <div className="lm-event-body">
                    <div className="lm-event-brand">{e.brand}</div>
                    <div className="lm-event-type" style={{color:e.color}}>{e.label}</div>
                  </div>
                  <div className="lm-event-day">{e.day}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="landing-section" id="features">
        <div className="landing-section-inner">
          <div className="landing-section-eyebrow">Everything you need</div>
          <h2 className="landing-h2">Built for creators who mean business</h2>
          <p className="landing-section-sub">No spreadsheets, no chasing invoices, no missed deadlines. One tool that handles the business side so you can focus on creating.</p>
          <div className="landing-features">
            {features.map((f, i) => (
              <div key={i} className="landing-feature-card">
                <div className="landing-feature-icon">{f.icon}</div>
                <div className="landing-feature-title">{f.title}</div>
                <div className="landing-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="landing-section landing-section-dark" id="how">
        <div className="landing-section-inner">
          <div className="landing-section-eyebrow" style={{color:'rgba(201,168,76,.5)'}}>Simple by design</div>
          <h2 className="landing-h2" style={{color:'#F7F4EF'}}>Up and running in minutes</h2>
          <div className="landing-steps">
            {[
              { n:'01', title:'Create your account', desc:'Sign up and set up your profile — your display name, rates card, invoice details and bank info. Takes 3 minutes.' },
              { n:'02', title:'Add your first collab', desc:'Drop in a brand name, deal type and value. Set your stage, deliverables and deadlines. Everything is tracked automatically.' },
              { n:'03', title:'Manage your business', desc:'Monitor earnings, get deadline alerts, generate invoices and share live progress links with brands — all from one dashboard.' },
            ].map((s, i) => (
              <div key={i} className="landing-step">
                <div className="landing-step-num">{s.n}</div>
                <div className="landing-step-body">
                  <div className="landing-step-title">{s.title}</div>
                  <div className="landing-step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="landing-section landing-cta-section">
        <div className="landing-section-inner landing-cta-inner">
          <div className="landing-orb landing-cta-orb"/>
          <div className="landing-section-eyebrow">Get started today</div>
          <h2 className="landing-h2">Your creator business,<br/><em>finally organised</em></h2>
          <button className="landing-cta-primary large" onClick={onSignup}>
            Create your free account
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <p className="landing-cta-note">Free to use · No credit card required</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-logo" style={{opacity:.5}}>
            <svg width="22" height="22" viewBox="0 0 44 44" fill="none">
              <rect width="44" height="44" rx="10" fill="url(#lf)"/>
              <defs><linearGradient id="lf" x1="0" y1="0" x2="44" y2="44" gradientUnits="userSpaceOnUse"><stop stopColor="#C9A84C"/><stop offset="1" stopColor="#8B7355"/></linearGradient></defs>
              <g transform="translate(22,22)" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="-12,1 -12,13 12,13 12,1" fill="rgba(255,255,255,0.2)"/>
                <polyline points="-12,1 -5,-4 5,-4 12,1"/>
                <line x1="0" y1="-4" x2="0" y2="1"/>
              </g>
            </svg>
            <span>My Creator Kit</span>
          </div>
          <div className="landing-footer-copy">© 2026 My Creator Kit · Built for creators</div>
        </div>
      </footer>
    </div>
  );
}

// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen({ defaultMode = 'login' }) {
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [show, setShow] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    if (mode === 'forgot') {
      if (!email.trim()) { setErr('Please enter your email address.'); setLoading(false); return; }
      const res = await apiForgotPassword(email);
      setLoading(false);
      if (res.ok) {
        setResetSent(true);
      } else {
        setErr(res.message || 'Something went wrong. Please try again.');
      }
      return;
    }
    if (mode === 'login') {
      const res = await apiLogin(email, password);
      if (!res.ok) setErr(res.message || 'Wrong email or password');
    } else {
      if (!handle.trim()) { setErr('Instagram handle is required'); setLoading(false); return; }
      const res = await apiRegister(email, password, handle, name);
      if (res.ok) setRegistered(true);
      else setErr(res.message || 'Registration failed');
    }
    setLoading(false);
  };

  if (registered) return (
    <div className="login-screen">
      <div className="login-blob1"/><div className="login-blob2"/><div className="login-blob3"/>
      <div className="login-card" style={{ textAlign:'center' }}>
        <div className="login-icon"><CheckCircle size={28}/></div>
        <h1 className="login-title">Request sent!</h1>
        <p className="login-sub" style={{ marginBottom:0 }}>Your account is pending admin approval. You'll receive an email once approved and ready to log in.</p>
      </div>
    </div>
  );

  if (resetSent) return (
    <div className="login-screen">
      <div className="login-blob1"/><div className="login-blob2"/><div className="login-blob3"/>
      <div className="login-card" style={{ textAlign:'center' }}>
        <div className="login-icon" style={{ background:'linear-gradient(135deg,#60A5FA,#818CF8)' }}><CheckCircle size={28}/></div>
        <h1 className="login-title">Check your email</h1>
        <p className="login-sub" style={{ marginBottom:'1rem' }}>We sent a password reset link to <strong>{email}</strong></p>
        <button className="login-btn" onClick={() => { setResetSent(false); setMode('login'); }}>Back to login</button>
      </div>
    </div>
  );

  return (
    <div className="login-screen">
      <div className="login-blob1"/><div className="login-blob2"/><div className="login-blob3"/>
      <div className="login-card">
        <div className="login-icon"><Sparkles size={28}/></div>
        <h1 className="login-title">My Creator Kit</h1>
        <p className="login-sub">
          {mode === 'login' ? 'Welcome back ✦' : mode === 'forgot' ? 'Reset your password' : 'Create your creator account'}
        </p>
        <form onSubmit={handleSubmit} className="login-form">
          {mode === 'register' && (
            <>
              <div className="pw-field">
                <User size={14} className="pw-icon"/>
                <input placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="pw-field">
                <Instagram size={14} className="pw-icon"/>
                <input placeholder="Instagram handle (e.g. its.blubeee)" value={handle} onChange={e => setHandle(e.target.value)} />
              </div>
            </>
          )}
          <div className="pw-field">
            <Lock size={14} className="pw-icon"/>
            <input type="email" placeholder="Email address" value={email} onChange={e => { setEmail(e.target.value); setErr(''); }} />
          </div>
          {mode !== 'forgot' && (
            <div className="pw-field">
              <Lock size={14} className="pw-icon"/>
              <input type={show ? 'text' : 'password'} placeholder="Password" value={password} onChange={e => { setPassword(e.target.value); setErr(''); }} />
              <button type="button" className="pw-eye" onClick={() => setShow(s => !s)}>{show ? <EyeOff size={14}/> : <Eye size={14}/>}</button>
            </div>
          )}
          {err && <div className="login-err">{err}</div>}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Please wait…' : mode === 'login' ? 'Log in' : mode === 'forgot' ? 'Send reset link' : 'Request access'}
          </button>
        </form>
        {mode === 'login' && (
          <button className="auth-switch" onClick={() => { setMode('forgot'); setErr(''); }}>Forgot password?</button>
        )}
        <button className="auth-switch" style={{ marginTop: mode === 'login' ? 4 : 12 }} onClick={() => { setMode(m => m === 'register' ? 'login' : m === 'forgot' ? 'login' : 'register'); setErr(''); }}>
          {mode === 'login' ? "Don't have an account? Sign up" : 'Back to login'}
        </button>
      </div>
    </div>
  );
}

// ─── PENDING SCREEN ────────────────────────────────────────────────────────────
function PendingScreen() {
  return (
    <div className="login-screen">
      <div className="login-blob1"/><div className="login-blob2"/><div className="login-blob3"/>
      <div className="login-card" style={{ textAlign:'center' }}>
        <div className="login-icon" style={{ background:'linear-gradient(135deg,#F59E0B,#F97316)' }}><AlertTriangle size={28}/></div>
        <h1 className="login-title">Pending approval</h1>
        <p className="login-sub">Your account is awaiting admin approval. You'll receive an email once you're approved.</p>
        <button className="login-btn" style={{ marginTop:'1rem' }} onClick={apiSignOut}>Sign out</button>
      </div>
    </div>
  );
}

// ─── ADMIN PANEL ──────────────────────────────────────────────────────────────
// ── EmailJS config — fill these in after setting up emailjs.com ──────────────
const EMAILJS_SERVICE_ID  = 'REPLACE_SERVICE_ID';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'REPLACE_TEMPLATE_ID';  // e.g. 'template_xyz789'
const EMAILJS_PUBLIC_KEY  = 'REPLACE_PUBLIC_KEY';   // e.g. 'user_AbCdEfGhIjK'
// ─────────────────────────────────────────────────────────────────────────────

async function sendApprovalEmail(toEmail, toName, handle) {
  try {
    const res = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id:  EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id:     EMAILJS_PUBLIC_KEY,
        template_params: {
          to_email:  toEmail,
          to_name:   toName || handle,
          handle:    handle,
          login_url: 'https://creatorkit-18506.web.app',
        },
      }),
    });
    return res.ok;
  } catch (e) {
    console.error('EmailJS error:', e);
    return false;
  }
}

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState({});
  const [emailStatus, setEmailStatus] = useState({}); // { uid: 'sent' | 'failed' }

  useEffect(() => { getAllUsers().then(u => { setUsers(u); setLoading(false); }); }, []);

  const handleApprove = async (u) => {
    setApproving(a => ({ ...a, [u.uid]: true }));
    await approveUser(u.uid, u.email);
    setUsers(prev => prev.map(x => x.uid === u.uid ? { ...x, approved: true } : x));
    // Send approval email
    const sent = await sendApprovalEmail(u.email, u.displayName, u.instagramHandle);
    setEmailStatus(s => ({ ...s, [u.uid]: sent ? 'sent' : 'failed' }));
    setApproving(a => ({ ...a, [u.uid]: false }));
  };

  if (loading) return <div className="page-content"><div className="loading-row"><div className="spinner"/></div></div>;

  const pending = users.filter(u => !u.approved);
  const approved = users.filter(u => u.approved);

  return (
    <div className="page-content">
      <div className="page-header"><h1 className="page-title">Users<span className="page-count">{users.length}</span></h1></div>
      {pending.length > 0 && (
        <>
          <div className="admin-section-label">Pending approval ({pending.length})</div>
          <div className="users-list" style={{ marginBottom:'1.5rem' }}>
            {pending.map(u => (
              <div key={u.uid} className="user-row pending">
                <Avatar name={u.displayName || u.instagramHandle} size={36}/>
                <div className="user-info">
                  <div className="user-name">{u.displayName || u.instagramHandle}</div>
                  <div className="user-meta">@{u.instagramHandle} · {u.email}</div>
                  <div className="user-meta" style={{ color:'var(--text3)' }}>Registered {new Date(u.createdAt).toLocaleDateString('en-AU')}</div>
                </div>
                <div className="user-actions">
                  {emailStatus[u.uid] === 'sent' ? (
                    <span className="due-chip ok"><Check size={11}/> Approved · Email sent</span>
                  ) : emailStatus[u.uid] === 'failed' ? (
                    <span className="due-chip warn">Approved · Email failed</span>
                  ) : (
                    <button className="btn-primary sm" disabled={approving[u.uid]} onClick={() => handleApprove(u)}>
                      <UserCheck size={13}/> {approving[u.uid] ? 'Approving…' : 'Approve'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="admin-section-label">Active users ({approved.length})</div>
      <div className="users-list">
        {approved.map(u => (
          <div key={u.uid} className="user-row">
            <Avatar name={u.displayName || u.instagramHandle} size={36}/>
            <div className="user-info">
              <div className="user-name">{u.displayName || u.instagramHandle}</div>
              <div className="user-meta">@{u.instagramHandle} · {u.email}</div>
            </div>
            <span className="due-chip ok"><Check size={11}/> Active</span>
          </div>
        ))}
        {approved.length === 0 && <div className="empty-state"><Users size={24}/><p>No active users yet.</p></div>}
      </div>
    </div>
  );
}

// ─── LINKS PAGE EDITOR ────────────────────────────────────────────────────────
function LinksEditor({ profile }) {
  const handle = profile?.instagramHandle;
  const [items, setItems] = useState([]);
  const [itemPhotos, setItemPhotos] = useState({}); // { itemId: base64 }
  const [config, setConfig] = useState({ tagline: 'skincare · food · travel · fashion', showInstagram: true, showWebsite: true, websiteUrl: '' });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const publicUrl = `${window.location.origin}/${handle}/links`;

  useEffect(() => {
    if (!handle) return;
    loadLinksPage(handle).then(({ config: cfg, items: its }) => {
      if (cfg) setConfig(c => ({ ...c, ...cfg }));
      setItems(its);
      setLoading(false);
      // Load photos
      getLinksPhoto(handle).then(p => { if (p) setProfilePhoto(p); });
      its.forEach(item => {
        getLinkItemPhoto(handle, item.id).then(p => {
          if (p) setItemPhotos(prev => ({ ...prev, [item.id]: p }));
        });
      });
    });
    // Also load profile photo from user account
    if (profile?.uid) getProfilePhoto(profile.uid).then(p => { if (p) setProfilePhoto(p); });
  }, [handle]);

  const handleSaveConfig = async () => {
    setSaving(true);
    await saveLinksConfig(handle, {
      ...config,
      handle,
      displayName: profile.displayName,
      instagramHandle: handle,
      profilePhotoBase64: profilePhoto || undefined,
    });
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSaveItem = async (item) => {
    // item.logoBase64 is set in modal, save it separately
    await saveLinkItem(handle, item);
    if (item.logoBase64) {
      setItemPhotos(prev => ({ ...prev, [item.id]: item.logoBase64 }));
    }
    setItems(prev => prev.find(x => x.id === item.id) ? prev.map(x => x.id === item.id ? item : x) : [...prev, item]);
    setEditModal(null);
  };

  const handleDeleteItem = async (id) => {
    await deleteLinkItem(handle, id);
    setItems(prev => prev.filter(x => x.id !== id));
    setItemPhotos(prev => { const n = {...prev}; delete n[id]; return n; });
    setEditModal(null);
  };

  const copyUrl = () => navigator.clipboard.writeText(publicUrl);

  if (loading) return <div className="page-content"><div className="loading-row"><div className="spinner"/></div></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <h1 className="page-title">My Links Page</h1>
        <a className="btn-ghost sm" href={`/${handle}/links`} target="_blank" rel="noreferrer"><ExternalLink size={13}/> Preview</a>
      </div>

      <div className="share-link-box" style={{ marginBottom:'1.5rem' }}>
        <div className="share-link-label"><Globe size={12}/> Your public link</div>
        <div className="share-link-url">{publicUrl}</div>
        <button className="share-link-copy" onClick={copyUrl}>Copy</button>
      </div>

      <div className="profile-section" style={{ marginBottom:'1rem' }}>
        <div className="section-card-title"><User size={13}/> Page settings</div>
        <div style={{display:'flex',alignItems:'flex-start',gap:16,marginBottom:14}}>
          <div>
            <div className="hint-text" style={{marginBottom:6}}>Profile photo</div>
            <PhotoUpload current={profilePhoto} onSelect={setProfilePhoto} size={72} round={true}/>
          </div>
          <div className="fg2" style={{flex:1}}>
            <Field label="Tagline"><input value={config.tagline || ''} onChange={e => setConfig(c => ({ ...c, tagline: e.target.value }))} placeholder="skincare · food · travel · fashion"/></Field>
            <Field label="Website URL"><input value={config.websiteUrl || ''} onChange={e => setConfig(c => ({ ...c, websiteUrl: e.target.value }))} placeholder="https://…"/></Field>
          </div>
        </div>
        <div style={{ display:'flex', gap:16, marginTop:10 }}>
          <label className="toggle-label">
            <input type="checkbox" checked={!!config.showInstagram} onChange={e => setConfig(c => ({ ...c, showInstagram: e.target.checked }))}/>
            <span className="toggle-slider"/><span>Show Instagram link</span>
          </label>
          <label className="toggle-label">
            <input type="checkbox" checked={!!config.showWebsite} onChange={e => setConfig(c => ({ ...c, showWebsite: e.target.checked }))}/>
            <span className="toggle-slider"/><span>Show website link</span>
          </label>
        </div>
        <button className="btn-primary" style={{ marginTop:14 }} onClick={handleSaveConfig} disabled={saving}>
          {saving ? 'Saving…' : saved ? <><Check size={13}/> Saved!</> : <><Save size={13}/> Save settings</>}
        </button>
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <div className="admin-section-label" style={{ margin:0 }}>Products & links ({items.length})</div>
        <button className="btn-primary" onClick={() => setEditModal({})}><Plus size={13}/> Add item</button>
      </div>

      {items.length === 0 && <div className="empty-state" style={{ padding:'2rem 0' }}><Globe size={24}/><p>No items yet. Add your first product or link.</p></div>}

      <div className="links-list">
        {items.map((item, idx) => {
          const cat = LINK_CATEGORIES.find(c => c.id === item.category);
          return (
            <div key={item.id} className="link-item-row">
              <div className="link-item-info">
                {(itemPhotos[item.id] || item.logoUrl) ? (
                  <img src={itemPhotos[item.id] || item.logoUrl} alt={item.brand} className="link-item-logo" onError={e => e.target.style.display='none'}/>
                ) : (
                  <div className="link-item-logo-placeholder">{item.brand?.slice(0,2).toUpperCase()}</div>
                )}
                <div>
                  <div className="link-item-brand">{item.brand}</div>
                  <div className="link-item-product">{item.productName}</div>
                  {cat && <span className="cat-tag-small" style={{ background:cat.bg, color:cat.color, border:`1px solid ${cat.border}` }}>{cat.label}</span>}
                  {item.couponCode && <span className="link-coupon-preview">{item.couponCode}</span>}
                </div>
              </div>
              <button className="btn-ghost sm" onClick={() => setEditModal(item)}><Edit3 size={12}/></button>
            </div>
          );
        })}
      </div>

      {editModal !== null && (
        <LinkItemModal
          item={Object.keys(editModal).length > 0 ? editModal : null}
          existingPhoto={editModal?.id ? itemPhotos[editModal.id] : null}
          onSave={handleSaveItem}
          onDelete={handleDeleteItem}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  );
}

function LinkItemModal({ item, onSave, onDelete, onClose, existingPhoto }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState(() => item || {
    id: uid(), brand: '', productName: '', category: 'skincare',
    couponCode: '', couponDesc: '', shopUrl: '',
    promoTitle: '', promoDesc: '', promoDates: '', order: 0,
  });
  const [logoPhoto, setLogoPhoto] = useState(existingPhoto || null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    onSave({ ...form, logoBase64: logoPhoto || undefined });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-hdr"><h2>{isEdit ? 'Edit item' : 'Add item'}</h2><button className="icon-btn" onClick={onClose}><X size={17}/></button></div>
        <div className="modal-body">
          <div className="fsec">Brand info</div>
          <div style={{display:'flex',alignItems:'flex-start',gap:14,marginBottom:12}}>
            <div>
              <div className="hint-text" style={{marginBottom:6}}>Brand logo</div>
              <PhotoUpload current={logoPhoto} onSelect={setLogoPhoto} size={60} round={false} label="Logo"/>
            </div>
            <div className="fg2" style={{flex:1}}>
              <Field label="Brand name *"><input value={form.brand} onChange={e => set('brand', e.target.value)} placeholder="e.g. Kormi" autoFocus/></Field>
              <Field label="Product name"><input value={form.productName} onChange={e => set('productName', e.target.value)} placeholder="e.g. Brightening Eye Cream"/></Field>
              <Field label="Category">
                <select value={form.category} onChange={e => set('category', e.target.value)}>
                  {LINK_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
            </div>
          </div>
          <div className="fsec" style={{ marginTop:'1.25rem' }}>Coupon & link</div>
          <div className="fg2">
            <Field label="Coupon code"><input value={form.couponCode || ''} onChange={e => set('couponCode', e.target.value)} placeholder="e.g. AASHI15"/></Field>
            <Field label="Discount description"><input value={form.couponDesc || ''} onChange={e => set('couponDesc', e.target.value)} placeholder="e.g. 15% off your order"/></Field>
          </div>
          <Field label="Shop URL" style={{ marginTop:10 }}><input value={form.shopUrl || ''} onChange={e => set('shopUrl', e.target.value)} placeholder="https://…"/></Field>
          <div className="fsec" style={{ marginTop:'1.25rem' }}>Promo banner (optional)</div>
          <Field label="Promo title"><input value={form.promoTitle || ''} onChange={e => set('promoTitle', e.target.value)} placeholder="e.g. Win a $50,000 Holiday!"/></Field>
          <Field label="Promo description" style={{ marginTop:10 }}><textarea value={form.promoDesc || ''} onChange={e => set('promoDesc', e.target.value)} rows={2} placeholder="Every $1 spent = 1 entry…"/></Field>
          <Field label="Promo dates" style={{ marginTop:10 }}><input value={form.promoDates || ''} onChange={e => set('promoDates', e.target.value)} placeholder="1 April – 30 April 2026"/></Field>
          <Field label="Display order" style={{ marginTop:10 }}><input type="number" value={form.order || 0} onChange={e => set('order', parseInt(e.target.value) || 0)} placeholder="0"/></Field>
        </div>
        <div className="modal-ftr">
          {isEdit && <button className="btn-danger" onClick={() => { if (window.confirm('Delete?')) onDelete(form.id); }}>Delete</button>}
          <div style={{ flex:1 }}/>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave}>{isEdit ? 'Save' : 'Add item'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── PUBLIC LINKS PAGE ────────────────────────────────────────────────────────
function PublicLinksPage() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [itemPhotos, setItemPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadLinksPage(handle).then(d => {
      setData(d);
      setLoading(false);
      // Load photos
      getLinksPhoto(handle).then(p => { if (p) setProfilePhoto(p); });
      d.items?.forEach(item => {
        getLinkItemPhoto(handle, item.id).then(p => {
          if (p) setItemPhotos(prev => ({ ...prev, [item.id]: p }));
        });
      });
    });
  }, [handle]);

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  if (loading) return <div className="share-loading"><div className="share-spinner"/></div>;
  if (!data?.config) return (
    <div className="share-notfound">
      <div className="share-notfound-icon">✦</div>
      <h2>Page not found</h2>
      <p>@{handle} hasn't set up their links page yet.</p>
    </div>
  );

  const { config, items } = data;
  const cats = [...new Set(items.map(i => i.category))].filter(Boolean);
  const filtered = filter === 'all' ? items : items.filter(i => i.category === filter);

  return (
    <div className="links-public-page">
      <div className="links-public-bg"/>
      <div className="links-public-orb1"/><div className="links-public-orb2"/>
      <div className="links-public-container">
        <div className="links-pub-header">
          <div className="links-pub-avatar-ring">
            <div className="links-pub-avatar-inner">
              {profilePhoto ? (
                <img src={profilePhoto} alt={handle} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}}/>
              ) : (
                <span>{handle?.slice(0,1).toUpperCase()}</span>
              )}
            </div>
          </div>
          <div className="links-pub-name">{config.displayName || handle}</div>
          <div className="links-pub-handle">@{handle}</div>
          {config.tagline && <div className="links-pub-tagline">{config.tagline}</div>}
          <div className="links-pub-social">
            {config.showInstagram && (
              <a className="links-social-btn links-social-ig" href={`https://instagram.com/${handle}`} target="_blank" rel="noopener">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                Instagram
              </a>
            )}
            {config.showWebsite && config.websiteUrl && (
              <a className="links-social-btn links-social-web" href={config.websiteUrl} target="_blank" rel="noopener">
                <Globe size={13}/> My website
              </a>
            )}
          </div>
        </div>

        <div className="links-pub-section-label">my faves & codes</div>

        {cats.length > 1 && (
          <div className="links-pub-filters">
            <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
            {cats.map(catId => {
              const cat = LINK_CATEGORIES.find(c => c.id === catId);
              return cat ? (
                <button key={catId} className={`filter-btn ${filter === catId ? 'active' : ''}`} onClick={() => setFilter(catId)}>{cat.label}</button>
              ) : null;
            })}
          </div>
        )}

        {filtered.length === 0 && <div style={{ textAlign:'center', color:'rgba(203,151,249,.4)', padding:'2rem 0', fontSize:13 }}>Nothing here yet ✦</div>}

        {filtered.map(item => {
          const cat = LINK_CATEGORIES.find(c => c.id === item.category);
          return (
            <div key={item.id} className="links-pub-card">
              {cat && <span className="links-pub-cat-tag" style={{ background:cat.bg, color:cat.color, border:`1px solid ${cat.border}` }}>{cat.label}</span>}
              <div className="links-pub-card-top">
                {(itemPhotos[item.id] || item.logoUrl) ? (
                  <img src={itemPhotos[item.id] || item.logoUrl} alt={item.brand} className="links-pub-logo" onError={e => e.target.style.display='none'}/>
                ) : (
                  <div className="links-pub-logo-placeholder">{item.brand?.slice(0,2).toUpperCase()}</div>
                )}
                <div className="links-pub-card-info">
                  <div className="links-pub-brand">{item.brand}</div>
                  <div className="links-pub-product">{item.productName}</div>
                </div>
              </div>
              {item.promoTitle && (
                <div className="links-pub-promo">
                  <div className="links-pub-promo-icon">🏖️</div>
                  <div>
                    <div className="links-pub-promo-title">{item.promoTitle}</div>
                    {item.promoDesc && <div className="links-pub-promo-desc">{item.promoDesc}</div>}
                    {item.promoDates && <div className="links-pub-promo-dates">{item.promoDates}</div>}
                  </div>
                </div>
              )}
              {item.couponCode && (
                <div className="links-pub-coupon" onClick={() => copyCode(item.couponCode, item.id)}>
                  <div className="links-pub-coupon-left">
                    <div className="links-pub-coupon-label">Your exclusive code</div>
                    <div className="links-pub-coupon-code">{item.couponCode}</div>
                    {item.couponDesc && <div className="links-pub-coupon-desc">{item.couponDesc}</div>}
                  </div>
                  <button className={`copy-btn ${copiedId === item.id ? 'copied' : ''}`}>
                    {copiedId === item.id ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              )}
              {item.shopUrl && (
                <a className="links-pub-shop-btn" href={item.shopUrl} target="_blank" rel="noopener">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
                  Shop now
                </a>
              )}
            </div>
          );
        })}
        <div className="links-pub-footer">My Creator Kit ✦</div>
      </div>
      <div className="links-toast" id="linksToast"/>
    </div>
  );
}

// ─── PHOTO UPLOAD ─────────────────────────────────────────────────────────────
function PhotoUpload({ current, onSelect, size = 80, label = 'Upload photo', round = true }) {
  const inputRef = useRef();
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Resize + compress to max 400px, quality 0.75
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 400;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL('image/jpeg', 0.75);
        onSelect(base64);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="photo-upload-wrap">
      <div
        className="photo-upload-preview"
        style={{ width: size, height: size, borderRadius: round ? '50%' : 14 }}
        onClick={() => inputRef.current.click()}
      >
        {current ? (
          <img src={current} alt="preview" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius: round ? '50%' : 14 }}/>
        ) : (
          <div className="photo-upload-empty">
            <Camera size={size > 60 ? 22 : 16}/>
            <span>{label}</span>
          </div>
        )}
        <div className="photo-upload-overlay"><Camera size={16}/></div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleFile}/>
    </div>
  );
}

// ─── PROFILE PAGE ─────────────────────────────────────────────────────────────
function ProfilePage({ profile, onUpdate }) {
  const [form, setForm] = useState(profile || {});
  const [rates, setRates] = useState(profile?.rates?.length ? profile.rates : DEFAULT_RATE_ITEMS.map(r => ({ ...r })));
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (profile?.uid) getProfilePhoto(profile.uid).then(p => { if (p) setPhoto(p); });
  }, [profile?.uid]);

  const [saveErr, setSaveErr] = useState('');

  const handleSave = async () => {
    setSaving(true); setSaveErr('');
    const res = await updateUserProfile(profile.uid, { ...form, rates });
    if (!res.ok) {
      setSaveErr(res.message);
      setSaving(false);
      return;
    }
    if (photo && photo.startsWith('data:')) {
      await saveProfilePhoto(profile.uid, photo);
      const handle = form.instagramHandle || profile.instagramHandle;
      if (handle) {
        await saveLinksConfig(handle, { profilePhotoBase64: photo });
      }
    }
    onUpdate({ ...form, rates });
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  const updateRate = (idx, field, val) => setRates(r => r.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  const addRate = () => setRates(r => [...r, { id: uid(), label: '', amount: '' }]);
  const removeRate = (idx) => setRates(r => r.filter((_, i) => i !== idx));

  return (
    <div className="page-content">
      <div className="page-header"><h1 className="page-title">My profile</h1></div>
      <div className="profile-grid">
        <div className="profile-section">
          <div className="section-card-title"><User size={14}/> Creator info</div>
          <div style={{display:'flex',alignItems:'flex-start',gap:16,marginBottom:14}}>
            <PhotoUpload current={photo} onSelect={setPhoto} size={72} label="Photo" round={true}/>
            <div style={{flex:1}}>
              <div className="fg2">
                <Field label="Display name"><input value={form.displayName||''} onChange={e=>set('displayName',e.target.value)}/></Field>
                <Field label="Instagram handle"><input value={form.instagramHandle||''} onChange={e=>set('instagramHandle',e.target.value)} placeholder="its.blubeee"/></Field>
              </div>
              <p className="hint-text" style={{marginTop:6}}>Profile photo is used on your public links page.</p>
            </div>
          </div>
        </div>
        <div className="profile-section">
          <div className="section-card-title"><Receipt size={14}/> Invoice details</div>
          <div className="fg2">
            <Field label="Full name"><input value={form.invoiceName||''} onChange={e=>set('invoiceName',e.target.value)}/></Field>
            <Field label="Email"><input value={form.invoiceEmail||''} onChange={e=>set('invoiceEmail',e.target.value)} type="email"/></Field>
            <Field label="Phone"><input value={form.invoicePhone||''} onChange={e=>set('invoicePhone',e.target.value)}/></Field>
            <Field label="ABN"><input value={form.invoiceAbn||''} onChange={e=>set('invoiceAbn',e.target.value)} placeholder="XX XXX XXX XXX"/></Field>
          </div>
          <Field label="Address" style={{marginTop:10}}><textarea value={form.invoiceAddress||''} onChange={e=>set('invoiceAddress',e.target.value)} rows={2} placeholder="Street, Suburb STATE Postcode"/></Field>
          <Field label="Bank name" style={{marginTop:10}}><input value={form.bankName||''} onChange={e=>set('bankName',e.target.value)} placeholder="e.g. CommBank"/></Field>
          <div className="fg2" style={{marginTop:10}}>
            <Field label="BSB"><input value={form.bankBsb||''} onChange={e=>set('bankBsb',e.target.value)} placeholder="062948"/></Field>
            <Field label="Account number"><input value={form.bankAccount||''} onChange={e=>set('bankAccount',e.target.value)}/></Field>
          </div>
          <Field label="Account name" style={{marginTop:10}}><input value={form.bankAccountName||''} onChange={e=>set('bankAccountName',e.target.value)}/></Field>
        </div>
        <div className="profile-section full-width">
          <div className="section-card-title"><DollarSign size={14}/> My rates</div>
          <p className="hint-text">Set your standard rates — these pre-fill when creating invoices.</p>
          <div className="rates-list">
            {rates.map((r, i) => (
              <div key={r.id||i} className="rate-row">
                <input className="rate-label-inp" value={r.label} onChange={e=>updateRate(i,'label',e.target.value)} placeholder="e.g. Instagram Reel"/>
                <div className="rate-amount-wrap">
                  <span className="rate-currency">AUD</span>
                  <input className="rate-amount-inp" type="number" value={r.amount} onChange={e=>updateRate(i,'amount',e.target.value)} placeholder="0.00"/>
                </div>
                <button className="icon-btn" onClick={()=>removeRate(i)}><Trash2 size={14}/></button>
              </div>
            ))}
            <button className="btn-ghost sm" onClick={addRate} style={{marginTop:6}}><Plus size={13}/> Add rate</button>
          </div>
        </div>
      </div>
      <div style={{marginTop:'1.5rem',display:'flex',flexDirection:'column',gap:10,alignItems:'flex-start'}}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : saved ? <><Check size={14}/> Saved!</> : <><Save size={14}/> Save profile</>}
        </button>
        {saveErr && <div className="login-err" style={{fontSize:13}}>{saveErr}</div>}
      </div>
    </div>
  );
}

// ─── TIPS PANEL ───────────────────────────────────────────────────────────────
function TipsPanel({ category, onClose }) {
  const tips = DELIVERABLE_TIPS[category] || DELIVERABLE_TIPS['Other'];
  return (
    <div className="tips-panel">
      <div className="tips-header">
        <div className="tips-title"><Lightbulb size={15}/> Tips for {category}</div>
        <button className="icon-btn" onClick={onClose}><X size={16}/></button>
      </div>
      <ul className="tips-list">
        {tips.map((tip, i) => <li key={i}><span className="tip-dot"/>{tip}</li>)}
      </ul>
    </div>
  );
}

// ─── COLLAB MODAL ─────────────────────────────────────────────────────────────
function CollabModal({ collab, onSave, onDelete, onClose }) {
  const isEdit = !!collab?.id;
  const [form, setForm] = useState(() => collab || {
    brand:'',cat:'Skincare',ig:'',tt:'',contact:'',email:'',
    type:'paid',value:'',priority:'normal',
    shoot:'home',delivery:'na',productReceivedDate:'',brandDeadlineDays:'',
    due:'',live:'',notes:'',stage:0,
    affiliateCode:'',affiliateLink:'',
    shootAddress:'',shootDate:'',shootTime:'',shootDone:false,
    needsApproval:false,approvalDueDate:'',approvalVideoLink:'',
    deliverables:[],
  });
  const [showTips, setShowTips] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addDeliverable = () => setForm(f => ({ ...f, deliverables: [...(f.deliverables||[]), { id: uid(), item:'', notes:'', preShoot:false, done:false }] }));
  const updateDeliv = (idx, field, val) => setForm(f => ({ ...f, deliverables: f.deliverables.map((d,i) => i===idx ? {...d,[field]:val} : d) }));
  const removeDeliv = (idx) => setForm(f => ({ ...f, deliverables: f.deliverables.filter((_,i) => i!==idx) }));

  const handleSave = () => {
    if (!form.brand.trim()) return;
    const now = new Date().toISOString();
    onSave({ ...form, id: form.id||uid(), shareId: form.shareId||generateShareId(), updatedAt:now, createdAt:form.createdAt||now });
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box wide">
        <div className="modal-hdr"><h2>{isEdit?'Edit collab':'New collab'}</h2><button className="icon-btn" onClick={onClose}><X size={17}/></button></div>
        <div className="modal-body">
          <div className="fsec">Brand info</div>
          <div className="fg2">
            <Field label="Brand name *"><input value={form.brand} onChange={e=>set('brand',e.target.value)} placeholder="e.g. Glow Lab" autoFocus/></Field>
            <Field label="Category"><select value={form.cat} onChange={e=>set('cat',e.target.value)}>{CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></Field>
            <Field label="Instagram"><input value={form.ig} onChange={e=>set('ig',e.target.value)} placeholder="@brand"/></Field>
            <Field label="TikTok"><input value={form.tt} onChange={e=>set('tt',e.target.value)} placeholder="@brand"/></Field>
            <Field label="Contact name"><input value={form.contact} onChange={e=>set('contact',e.target.value)}/></Field>
            <Field label="Contact email"><input value={form.email} onChange={e=>set('email',e.target.value)} type="email"/></Field>
          </div>
          <div className="fsec" style={{marginTop:'1.25rem'}}>Deal details</div>
          <div className="fg2">
            <Field label="Type"><select value={form.type} onChange={e=>set('type',e.target.value)}>{COLLAB_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}</select></Field>
            <Field label="Value (AUD)"><input value={form.value} onChange={e=>set('value',e.target.value)} type="number" placeholder="0 for barter"/></Field>
            <Field label="Priority"><select value={form.priority||'normal'} onChange={e=>set('priority',e.target.value)}>{PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}</select></Field>
            <Field label="Stage"><select value={form.stage} onChange={e=>set('stage',parseInt(e.target.value))}>{STAGES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}</select></Field>
            <Field label="Shoot location"><select value={form.shoot} onChange={e=>set('shoot',e.target.value)}>{SHOOT_LOCATIONS.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}</select></Field>
            <Field label="Product delivery"><select value={form.delivery} onChange={e=>set('delivery',e.target.value)}>{DELIVERY_STATUS.map(d=><option key={d.id} value={d.id}>{d.label}</option>)}</select></Field>
          </div>
          {(form.shoot==='brand'||form.shoot==='both')&&(
            <div className="location-shoot-box">
              <div className="location-shoot-label"><Camera size={12}/> Shoot location details</div>
              <Field label="Venue address"><input value={form.shootAddress||''} onChange={e=>set('shootAddress',e.target.value)} placeholder="e.g. 123 Collins St, Melbourne VIC 3000"/></Field>
              <div className="fg2" style={{marginTop:10}}>
                <Field label="Appointment date"><input value={form.shootDate||''} onChange={e=>set('shootDate',e.target.value)} type="date"/></Field>
                <Field label="Appointment time"><input value={form.shootTime||''} onChange={e=>set('shootTime',e.target.value)} type="time"/></Field>
              </div>
              <div style={{marginTop:10}}>
                <label className="toggle-label">
                  <input type="checkbox" checked={!!form.shootDone} onChange={e=>set('shootDone',e.target.checked)}/>
                  <span className="toggle-slider"/><span>Shoot completed</span>
                </label>
              </div>
            </div>
          )}
          <div className="fsec" style={{marginTop:'1.25rem'}}><Timer size={11} style={{marginRight:5,verticalAlign:'middle'}}/>Brand delivery deadline</div>
          <div className="deadline-hint">Some brands require content within X days of receiving the product.</div>
          <div className="fg2">
            <Field label="Product received date"><input value={form.productReceivedDate||''} onChange={e=>set('productReceivedDate',e.target.value)} type="date"/></Field>
            <Field label="Deliver within (days)"><input value={form.brandDeadlineDays||''} onChange={e=>set('brandDeadlineDays',e.target.value)} type="number" placeholder="e.g. 7"/></Field>
          </div>
          <div className="fsec" style={{marginTop:'1.25rem'}}>Dates</div>
          <div className="fg2">
            <Field label="Content due"><input value={form.due} onChange={e=>set('due',e.target.value)} type="date"/></Field>
            <Field label="Go-live date"><input value={form.live} onChange={e=>set('live',e.target.value)} type="date"/></Field>
          </div>
          <div className="fsec" style={{marginTop:'1.25rem'}}><CheckCircle size={11} style={{marginRight:5,verticalAlign:'middle'}}/>Needs approval</div>
          <div className="approval-toggle-row">
            <label className="toggle-label">
              <input type="checkbox" checked={!!form.needsApproval} onChange={e=>set('needsApproval',e.target.checked)}/>
              <span className="toggle-slider"/><span>Brand requires video approval before delivery</span>
            </label>
          </div>
          {form.needsApproval&&(
            <div className="fg2" style={{marginTop:10}}>
              <Field label="Approval video due date"><input value={form.approvalDueDate||''} onChange={e=>set('approvalDueDate',e.target.value)} type="date"/></Field>
              <Field label="Approval video link"><input value={form.approvalVideoLink||''} onChange={e=>set('approvalVideoLink',e.target.value)} placeholder="Drive / Dropbox link"/></Field>
            </div>
          )}
          <div className="fsec" style={{marginTop:'1.25rem'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%'}}>
              <span><Package size={11} style={{marginRight:5,verticalAlign:'middle'}}/>Deliverables</span>
              <button className="tips-btn" onClick={()=>setShowTips(s=>!s)}><Lightbulb size={12}/> Tips for {form.cat}</button>
            </div>
          </div>
          {showTips&&<TipsPanel category={form.cat} onClose={()=>setShowTips(false)}/>}
          <div className="delivs-builder">
            {(form.deliverables||[]).map((d,i)=>(
              <div key={d.id||i} className="deliv-builder-row">
                <div className="deliv-builder-main">
                  <input className="deliv-item-inp" value={d.item} onChange={e=>updateDeliv(i,'item',e.target.value)} placeholder="e.g. 1x Reel — no unboxing, high quality"/>
                  <label className="pre-shoot-label"><input type="checkbox" checked={!!d.preShoot} onChange={e=>updateDeliv(i,'preShoot',e.target.checked)}/>Pre-shoot req.</label>
                  <button className="icon-btn" onClick={()=>removeDeliv(i)}><Trash2 size={13}/></button>
                </div>
                <input className="deliv-notes-inp" value={d.notes||''} onChange={e=>updateDeliv(i,'notes',e.target.value)} placeholder="Notes (optional)"/>
              </div>
            ))}
            <button className="btn-ghost sm" onClick={addDeliverable} style={{marginTop:6}}><Plus size={13}/> Add deliverable</button>
          </div>
          <div className="fsec" style={{marginTop:'1.25rem'}}>Notes / brief</div>
          <Field label=""><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Talking points, vibe, dos & don'ts…" rows={3}/></Field>
          <div className="fsec" style={{marginTop:'1.25rem'}}>Affiliate / coupon</div>
          <div className="fg2">
            <Field label="Coupon code"><input value={form.affiliateCode||''} onChange={e=>set('affiliateCode',e.target.value)} placeholder="e.g. AASHI10"/></Field>
            <Field label="Affiliate link"><input value={form.affiliateLink||''} onChange={e=>set('affiliateLink',e.target.value)} placeholder="https://…"/></Field>
          </div>
        </div>
        <div className="modal-ftr">
          {isEdit&&<button className="btn-danger" onClick={()=>{if(window.confirm('Delete?'))onDelete(form.id);}}>Delete</button>}
          <div style={{flex:1}}/>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave}>{isEdit?'Save changes':'Add collab'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── INVOICE MODAL + PDF ──────────────────────────────────────────────────────
function InvoiceModal({ collab, profile, existingInvoice, onSave, onClose }) {
  const today = new Date().toISOString().slice(0,10);
  const due14 = new Date(Date.now()+14*86400000).toISOString().slice(0,10);
  const [form, setForm] = useState(() => existingInvoice || {
    id: uid(), collabId: collab?.id||'', brandName: collab?.brand||'',
    invoiceNumber:'', invoiceDate:today, dueDate:due14, paymentType:'single',
    items: profile?.rates?.filter(r=>r.amount).map(r=>({id:r.id||uid(),label:r.label,qty:1,amount:r.amount})) || [{ id:uid(), label:'', qty:1, amount:'' }],
    milestones: [{ id:uid(), label:'Upfront', amount:'', paid:false, paidDate:'' }],
    alreadyPaid:false, paidDate:'', notes:'', status:'draft',
  });
  useEffect(() => {
    if (!existingInvoice) getNextInvoiceNumber(profile.uid).then(n => setForm(f=>({...f,invoiceNumber:n})));
  }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const total = form.paymentType==='milestone'
    ? form.milestones.reduce((a,m)=>a+(parseFloat(m.amount||0)),0)
    : form.items.reduce((a,i)=>a+(parseFloat(i.amount||0)*parseFloat(i.qty||1)),0);
  const updateItem=(idx,field,val)=>setForm(f=>({...f,items:f.items.map((x,i)=>i===idx?{...x,[field]:val}:x)}));
  const addItem=()=>setForm(f=>({...f,items:[...f.items,{id:uid(),label:'',qty:1,amount:''}]}));
  const removeItem=(idx)=>setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)}));
  const updateMs=(idx,field,val)=>setForm(f=>({...f,milestones:f.milestones.map((x,i)=>i===idx?{...x,[field]:val}:x)}));
  const addMs=()=>setForm(f=>({...f,milestones:[...f.milestones,{id:uid(),label:'',amount:'',paid:false,paidDate:''}]}));
  const removeMs=(idx)=>setForm(f=>({...f,milestones:f.milestones.filter((_,i)=>i!==idx)}));
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box wide">
        <div className="modal-hdr"><h2>{existingInvoice?'Edit invoice':'Create invoice'}</h2><button className="icon-btn" onClick={onClose}><X size={17}/></button></div>
        <div className="modal-body">
          <div className="fsec">Invoice details</div>
          <div className="fg2">
            <Field label="Invoice number"><input value={form.invoiceNumber} onChange={e=>set('invoiceNumber',e.target.value)}/></Field>
            <Field label="Brand / Bill to"><input value={form.brandName} onChange={e=>set('brandName',e.target.value)}/></Field>
            <Field label="Invoice date"><input value={form.invoiceDate} onChange={e=>set('invoiceDate',e.target.value)} type="date"/></Field>
            <Field label="Due date"><input value={form.dueDate} onChange={e=>set('dueDate',e.target.value)} type="date"/></Field>
          </div>
          <div className="fsec" style={{marginTop:'1.25rem'}}>Payment type</div>
          <div className="payment-type-row">
            {['single','milestone'].map(pt=>(
              <button key={pt} className={`payment-type-btn ${form.paymentType===pt?'active':''}`} onClick={()=>set('paymentType',pt)}>
                {pt==='single'?'Single payment':'Milestone payments'}
              </button>
            ))}
          </div>
          {form.paymentType==='single'&&(
            <>
              <div className="fsec" style={{marginTop:'1.25rem'}}>Line items</div>
              {form.items.map((item,i)=>(
                <div key={item.id||i} className="invoice-item-row">
                  <input className="inv-desc" value={item.label} onChange={e=>updateItem(i,'label',e.target.value)} placeholder="Description"/>
                  <input className="inv-qty" type="number" value={item.qty} onChange={e=>updateItem(i,'qty',e.target.value)} placeholder="Qty"/>
                  <input className="inv-amount" type="number" value={item.amount} onChange={e=>updateItem(i,'amount',e.target.value)} placeholder="0.00"/>
                  <button className="icon-btn" onClick={()=>removeItem(i)}><Trash2 size={13}/></button>
                </div>
              ))}
              <button className="btn-ghost sm" onClick={addItem} style={{marginTop:6}}><Plus size={13}/> Add item</button>
              <div className="inv-already-paid">
                <label className="toggle-label">
                  <input type="checkbox" checked={!!form.alreadyPaid} onChange={e=>set('alreadyPaid',e.target.checked)}/>
                  <span className="toggle-slider"/><span>Already paid</span>
                </label>
                {form.alreadyPaid&&<input type="date" value={form.paidDate||''} onChange={e=>set('paidDate',e.target.value)} style={{marginLeft:12}}/>}
              </div>
            </>
          )}
          {form.paymentType==='milestone'&&(
            <>
              <div className="fsec" style={{marginTop:'1.25rem'}}>Milestones</div>
              {form.milestones.map((ms,i)=>(
                <div key={ms.id||i} className="milestone-row">
                  <input className="ms-label" value={ms.label} onChange={e=>updateMs(i,'label',e.target.value)} placeholder="e.g. Upfront, On delivery…"/>
                  <input className="ms-amount" type="number" value={ms.amount} onChange={e=>updateMs(i,'amount',e.target.value)} placeholder="0.00"/>
                  <label className="ms-paid-label"><input type="checkbox" checked={!!ms.paid} onChange={e=>updateMs(i,'paid',e.target.checked)}/>Paid</label>
                  {ms.paid&&<input type="date" className="ms-date" value={ms.paidDate||''} onChange={e=>updateMs(i,'paidDate',e.target.value)}/>}
                  <button className="icon-btn" onClick={()=>removeMs(i)}><Trash2 size={13}/></button>
                </div>
              ))}
              <button className="btn-ghost sm" onClick={addMs} style={{marginTop:6}}><Plus size={13}/> Add milestone</button>
            </>
          )}
          <div className="inv-total-row"><span>Total</span><strong>AUD {total.toFixed(2)}</strong></div>
          <div className="fsec" style={{marginTop:'1.25rem'}}>Notes</div>
          <Field label=""><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="e.g. No GST applicable" rows={2}/></Field>
        </div>
        <div className="modal-ftr">
          <div style={{flex:1}}/>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={()=>onSave(form)}>Save invoice</button>
        </div>
      </div>
    </div>
  );
}

function generateInvoicePDF(invoice, profile) {
  const total = invoice.paymentType==='milestone'
    ? invoice.milestones.reduce((a,m)=>a+(parseFloat(m.amount||0)),0)
    : invoice.items.reduce((a,i)=>a+(parseFloat(i.amount||0)*parseFloat(i.qty||1)),0);
  const itemsHtml = invoice.paymentType==='milestone'
    ? invoice.milestones.map(m=>`<tr><td class="desc"><strong>${m.label}</strong></td><td class="qty">1</td><td class="price">AUD ${parseFloat(m.amount||0).toFixed(2)}</td><td class="amount">AUD ${parseFloat(m.amount||0).toFixed(2)}</td></tr>${m.paid?`<tr><td colspan="4" class="paid-note">✓ Paid${m.paidDate?' on '+m.paidDate:''}</td></tr>`:''}`).join('')
    : invoice.items.map(i=>`<tr><td class="desc"><strong>${i.label}</strong></td><td class="qty">${i.qty||1}</td><td class="price">AUD ${parseFloat(i.amount||0).toFixed(2)}</td><td class="amount">AUD ${(parseFloat(i.amount||0)*parseFloat(i.qty||1)).toFixed(2)}</td></tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1A1612;font-size:13px;padding:40px}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;border-bottom:2px solid #C9A84C;padding-bottom:24px}.from-name{font-size:22px;font-weight:700;color:#C9A84C;margin-bottom:2px}.from-sub{font-size:12px;color:#8B7D6B}.inv-title{font-size:28px;font-weight:700;color:#C9A84C;text-align:right}.inv-num{font-size:13px;color:#8B7D6B;text-align:right}.meta-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;margin-bottom:28px}.meta-label{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#9B8E7E;margin-bottom:3px;font-weight:600}.meta-val{font-size:13px;font-weight:500;color:#1A1612;line-height:1.5}table{width:100%;border-collapse:collapse;margin-bottom:24px}thead tr{background:#C9A84C;color:#0F0C09}thead th{padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em}td{padding:10px 12px;border-bottom:0.5px solid #E4DDD0}.qty,.price,.amount{text-align:right}.paid-note{font-size:11px;color:#059669;background:#D1FAE5;padding:4px 12px}.total-row{display:flex;justify-content:flex-end;margin-bottom:24px}.total-box{background:#C9A84C;color:#0F0C09;padding:14px 20px;border-radius:8px;text-align:right;min-width:200px}.total-label{font-size:11px;text-transform:uppercase;letter-spacing:.06em;opacity:.85}.total-amount{font-size:20px;font-weight:700;margin-top:2px}.gst-note{font-size:11px;color:#9B8E7E;font-style:italic;margin-bottom:24px}.payment-section{border-top:1px solid #E4DDD0;padding-top:20px;margin-bottom:24px}.payment-title{font-size:12px;font-weight:600;color:#C9A84C;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px}.payment-detail{font-size:13px;line-height:2}.footer{text-align:center;font-size:12px;color:#9B8E7E;border-top:1px solid #E4DDD0;padding-top:16px;font-style:italic}.status-paid{display:inline-block;background:#D1FAE5;color:#059669;font-size:12px;font-weight:600;padding:4px 12px;border-radius:20px;margin-bottom:16px}</style></head><body>
  <div class="header"><div><div class="from-name">${profile.invoiceName||profile.displayName}</div><div class="from-sub">Instagram Content Creator</div></div><div><div class="inv-title">INVOICE</div><div class="inv-num">${invoice.invoiceNumber}</div></div></div>
  <div class="meta-grid"><div><div class="meta-label">From</div><div class="meta-val">${profile.invoiceName||profile.displayName}<br>${(profile.invoiceAddress||'').replace(/\n/g,'<br>')}<br>${profile.invoiceEmail||''}<br>${profile.invoicePhone||''}<br>${profile.invoiceAbn?'ABN: '+profile.invoiceAbn:''}</div></div><div><div class="meta-label">Bill to</div><div class="meta-val"><strong>${invoice.brandName}</strong></div></div><div><div class="meta-label">Details</div><div class="meta-val"><strong>Invoice Date:</strong> ${invoice.invoiceDate}<br><strong>Due Date:</strong> ${invoice.dueDate}</div></div></div>
  ${invoice.alreadyPaid?`<div class="status-paid">✓ PAID${invoice.paidDate?' — '+invoice.paidDate:''}</div>`:''}
  <table><thead><tr><th>Description</th><th style="text-align:right">QTY</th><th style="text-align:right">Unit price</th><th style="text-align:right">Amount</th></tr></thead><tbody>${itemsHtml}</tbody></table>
  <div class="total-row"><div class="total-box"><div class="total-label">Total due</div><div class="total-amount">AUD ${total.toFixed(2)}</div></div></div>
  ${invoice.notes?`<div class="gst-note">* ${invoice.notes}</div>`:''}
  ${profile.bankName?`<div class="payment-section"><div class="payment-title">Payment details</div><div class="payment-detail">Please transfer payment within 14 days of invoice date.<br><br><strong>Bank:</strong> ${profile.bankName}<br><strong>Account Name:</strong> ${profile.bankAccountName||profile.invoiceName}<br><strong>BSB:</strong> ${profile.bankBsb}<br><strong>Account Number:</strong> ${profile.bankAccount}<br><strong>Reference:</strong> ${invoice.invoiceNumber} / ${invoice.brandName}</div></div>`:''}
  <div class="footer">Thank you for this collaboration! ✦ @${profile.instagramHandle}</div>
  </body></html>`;
  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 500);
}

// ─── INVOICES PAGE ────────────────────────────────────────────────────────────
function InvoicesPage({ uid: userUid, profile, collabs }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  useEffect(() => { loadInvoices(userUid).then(d=>{ setInvoices(d); setLoading(false); }); }, [userUid]);
  const handleSave = async (inv) => {
    await saveInvoice(userUid, inv);
    setInvoices(prev => prev.find(x=>x.id===inv.id) ? prev.map(x=>x.id===inv.id?inv:x) : [...prev,inv]);
    setModal(null);
  };
  const handleDelete = async (id) => { await deleteInvoice(userUid, id); setInvoices(prev=>prev.filter(x=>x.id!==id)); setModal(null); };
  const getInvTotal = (inv) => inv.paymentType==='milestone' ? inv.milestones?.reduce((a,m)=>a+(parseFloat(m.amount||0)),0)||0 : inv.items?.reduce((a,i)=>a+(parseFloat(i.amount||0)*parseFloat(i.qty||1)),0)||0;
  if (loading) return <div className="page-content"><div className="loading-row"><div className="spinner"/></div></div>;
  return (
    <div className="page-content">
      <div className="page-header"><h1 className="page-title">Invoices<span className="page-count">{invoices.length}</span></h1><button className="btn-primary" onClick={()=>setModal({collab:null})}><Plus size={14}/> New invoice</button></div>
      {invoices.length===0 ? <div className="empty-state"><Receipt size={28}/><p>No invoices yet.</p></div> : (
        <table className="list-tbl">
          <thead><tr><th>Invoice #</th><th>Brand</th><th>Date</th><th>Due</th><th>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>{invoices.map(inv=>{
            const total=getInvTotal(inv);
            const isPaid=inv.alreadyPaid||(inv.paymentType==='milestone'&&inv.milestones?.every(m=>m.paid));
            return <tr key={inv.id}>
              <td style={{fontWeight:500}}>{inv.invoiceNumber}</td><td>{inv.brandName}</td><td>{inv.invoiceDate}</td>
              <td><DueBadge dateStr={inv.dueDate}/></td><td className="list-val">AUD {total.toFixed(2)}</td>
              <td>{isPaid?<span className="due-chip ok"><Check size={10}/> Paid</span>:<span className="due-chip warn">Unpaid</span>}</td>
              <td><div style={{display:'flex',gap:6}}>
                <button className="btn-ghost sm" onClick={()=>setModal({invoice:inv})}><Edit3 size={12}/></button>
                <button className="btn-purple sm" onClick={()=>generateInvoicePDF(inv,profile)}><FileText size={12}/> PDF</button>
                <button className="icon-btn" onClick={()=>{if(window.confirm('Delete?'))handleDelete(inv.id)}}><Trash2 size={13}/></button>
              </div></td>
            </tr>;
          })}</tbody>
        </table>
      )}
      {modal&&<InvoiceModal collab={modal.collab||null} profile={profile} existingInvoice={modal.invoice||null} onSave={handleSave} onClose={()=>setModal(null)}/>}
    </div>
  );
}

// ─── CAMPAIGN MODAL ──────────────────────────────────────────────────────────
function CampaignModal({ campaign, collabs, onSave, onDelete, onClose }) {
  const isEdit = !!campaign?.id;
  const [f, setF] = useState(() => campaign || { id: uid(), name: '', description: '', collabIds: [] });
  const toggle = (id) => setF(prev => ({
    ...prev,
    collabIds: prev.collabIds.includes(id)
      ? prev.collabIds.filter(x => x !== id)
      : [...prev.collabIds, id]
  }));
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-hdr">
          <h2>{isEdit ? 'Edit campaign' : 'New campaign'}</h2>
          <button className="icon-btn" onClick={onClose}><X size={17}/></button>
        </div>
        <div className="modal-body">
          <Field label="Campaign name"><input value={f.name} onChange={e=>setF(p=>({...p,name:e.target.value}))} autoFocus/></Field>
          <Field label="Description" style={{marginTop:10}}><textarea value={f.description||''} onChange={e=>setF(p=>({...p,description:e.target.value}))} rows={2}/></Field>
          <div className="fsec" style={{marginTop:'1.25rem'}}>Link collabs</div>
          <div className="campaign-collabs-list">
            {collabs.map(c => (
              <label key={c.id} className="campaign-collab-item">
                <input type="checkbox" checked={f.collabIds.includes(c.id)} onChange={() => toggle(c.id)}/>
                <Avatar name={c.brand} size={22}/>
                <span>{c.brand}</span>
                <TypeBadge type={c.type} small/>
              </label>
            ))}
          </div>
        </div>
        <div className="modal-ftr">
          {isEdit && <button className="btn-danger" onClick={() => { if (window.confirm('Delete?')) onDelete(f.id); }}>Delete</button>}
          <div style={{flex:1}}/>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => onSave(f)}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── CAMPAIGNS PAGE ───────────────────────────────────────────────────────────
function CampaignsPage({ uid: userUid, collabs }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} (new) | campaign obj (edit)
  const [expanded, setExpanded] = useState({});
  useEffect(() => { loadCampaigns(userUid).then(d=>{ setCampaigns(d); setLoading(false); }); }, [userUid]);
  const handleSave = async (c) => { await saveCampaign(userUid,c); setCampaigns(prev=>prev.find(x=>x.id===c.id)?prev.map(x=>x.id===c.id?c:x):[...prev,c]); setModal(null); };
  const handleDelete = async (id) => { await deleteCampaign(userUid,id); setCampaigns(prev=>prev.filter(x=>x.id!==id)); setModal(null); };
  if (loading) return <div className="page-content"><div className="loading-row"><div className="spinner"/></div></div>;
  return (
    <div className="page-content">
      <div className="page-header">
        <h1 className="page-title">Campaigns<span className="page-count">{campaigns.length}</span></h1>
        <button className="btn-primary" onClick={()=>setModal({})}><Plus size={14}/> New campaign</button>
      </div>
      {campaigns.length===0 ? <div className="empty-state"><Link size={28}/><p>No campaigns yet. Link collabs from the same brand together.</p></div> : (
        <div className="campaigns-list">{campaigns.map(camp=>{
          const linked=collabs.filter(c=>camp.collabIds?.includes(c.id));
          const totalVal=linked.reduce((a,c)=>a+(parseFloat(c.value)||0),0);
          const avgProgress=linked.length?Math.round(linked.reduce((a,c)=>a+stagePct(c.stage),0)/linked.length):0;
          const isExp=expanded[camp.id];
          return <div key={camp.id} className="campaign-card">
            <div className="campaign-header" onClick={()=>setExpanded(e=>({...e,[camp.id]:!e[camp.id]}))}>
              <div className="campaign-left"><div className="campaign-name">{camp.name}</div>{camp.description&&<div className="campaign-desc">{camp.description}</div>}</div>
              <div className="campaign-stats">
                <span className="campaign-stat">{linked.length} collabs</span>
                {totalVal>0&&<span className="campaign-stat">${totalVal.toLocaleString()} AUD</span>}
                <div className="campaign-progress"><div className="pbar-track" style={{width:80}}><div className="pbar-fill" style={{width:`${avgProgress}%`,background:'var(--p)'}}/></div><span className="pbar-pct" style={{color:'var(--p)'}}>{avgProgress}%</span></div>
                <button className="icon-btn" onClick={e=>{e.stopPropagation();setModal(camp);}}><Edit3 size={13}/></button>
                {isExp?<ChevronUp size={16}/>:<ChevronDown size={16}/>}
              </div>
            </div>
            {isExp&&<div className="campaign-collabs">
              {linked.length===0&&<div style={{fontSize:12,color:'var(--text3)',padding:'8px 0'}}>No collabs linked yet.</div>}
              {linked.map(c=><div key={c.id} className="campaign-collab-row"><Avatar name={c.brand} size={26}/><span className="campaign-collab-brand">{c.brand}</span><TypeBadge type={c.type} small/><StageBadge stage={c.stage} small/><ProgressBar stage={c.stage} collab={c} showLabel/>{c.due&&<DueBadge dateStr={c.due}/>}</div>)}
            </div>}
          </div>;
        })}</div>
      )}
      {modal !== null && (
        <CampaignModal
          campaign={Object.keys(modal).length > 0 ? modal : null}
          collabs={collabs}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ─── KANBAN CARD ──────────────────────────────────────────────────────────────
function KanbanCard({ collab, onClick }) {
  const bd = brandDeadlineInfo(collab);
  const ep = effPriority(collab);
  const urgentColor = { urgent:'#EF4444', high:'#F59E0B' }[ep];
  const preShootItems = (collab.deliverables||[]).filter(d=>d.preShoot);
  return (
    <div className="kcard" style={urgentColor?{borderTopColor:urgentColor,borderTopWidth:3}:{}} onClick={()=>onClick(collab)}>
      <div className="kcard-top">
        <Avatar name={collab.brand} size={28}/>
        <div className="kcard-info"><div className="kcard-brand">{collab.brand}</div><div className="kcard-cat">{collab.cat}</div></div>
        {urgentColor&&<Flag size={12} style={{color:urgentColor,flexShrink:0}}/>}
        {collab.needsApproval&&<CheckCircle size={12} style={{color:'#F97316',flexShrink:0}} title="Needs approval"/>}
      </div>
      <ProgressBar stage={collab.stage} collab={collab} showLabel/>
      <div className="kcard-tags">
        <TypeBadge type={collab.type} small/>
        {collab.value>0&&<span className="kcard-val">${parseFloat(collab.value).toLocaleString()}</span>}
      </div>
      {bd?<div style={{marginTop:5}}><BrandDlBadge collab={collab}/></div>:collab.due?<div style={{marginTop:5}}><DueBadge dateStr={collab.due}/></div>:null}
      {preShootItems.length>0&&<div className="kcard-preshoot"><AlertTriangle size={10}/> {preShootItems.length} pre-shoot req.</div>}
      {(collab.deliverables||[]).length>0&&(
        <div className="kcard-delivs">
          {collab.deliverables.slice(0,2).map((d,i)=><div key={i} className="kcard-deliv-row"><div className="deliv-dot"/><span>{d.item}</span></div>)}
          {collab.deliverables.length>2&&<div className="kcard-deliv-more">+{collab.deliverables.length-2} more</div>}
        </div>
      )}
    </div>
  );
}

// ─── DETAIL VIEW ──────────────────────────────────────────────────────────────
function DetailView({ collab, onBack, onEdit, onStageChange, onCreateInvoice }) {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const bd = brandDeadlineInfo(collab);
  const pct = stagePct(collab.stage);
  const s = STAGES[collab.stage]||STAGES[0];
  const barColor = pct===100?'#059669':pct>=66?'#10B981':'#C9A84C';
  const shareUrl = `${window.location.origin}/share/${collab.shareId}`;
  const preShootItems = (collab.deliverables||[]).filter(d=>d.preShoot);
  const regularItems = (collab.deliverables||[]).filter(d=>!d.preShoot);
  const copyShare = () => navigator.clipboard.writeText(shareUrl).then(()=>{ setLinkCopied(true); setTimeout(()=>setLinkCopied(false),2000); });
  const copyText = () => {
    const txt = [`Collab summary — ${collab.brand}`, '', `Stage: ${s.label} (${pct}%)`,
      collab.due?`Due: ${collab.due}`:null, bd?`Brand deadline: ${bd.deadline}`:null,
      `\nFull details: ${shareUrl}`].filter(Boolean).join('\n');
    navigator.clipboard.writeText(txt).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); });
  };
  return (
    <div className="detail-view">
      <button className="back-btn" onClick={onBack}><ArrowLeft size={14}/> Back</button>
      <div className="detail-hero">
        <div className="detail-hero-left">
          <Avatar name={collab.brand} size={52}/>
          <div>
            <div style={{marginBottom:5,display:'flex',gap:6,flexWrap:'wrap'}}>
              <PriorityBadge priority={collab.priority||'normal'} small/>
              {collab.needsApproval&&<span className="badge sm" style={{background:'#FFF7ED',color:'#C2410C'}}>Needs approval</span>}
            </div>
            <h1 className="detail-brand">{collab.brand}</h1>
            <div className="detail-meta">
              <TypeBadge type={collab.type}/><span className="detail-cat">{collab.cat}</span>
              {collab.ig&&<span className="detail-handle"><Instagram size={11}/>{collab.ig}</span>}
            </div>
          </div>
        </div>
        <div className="detail-actions">
          <button className="btn-ghost sm" onClick={copyText}>{copied?<><Check size={13}/> Copied!</>:<><Copy size={13}/> Copy</>}</button>
          <button className="btn-purple sm" onClick={copyShare}>{linkCopied?<><Check size={13}/> Copied!</>:<><Share2 size={13}/> Share link</>}</button>
          <button className="btn-ghost sm" onClick={()=>onCreateInvoice(collab)}><Receipt size={13}/> Invoice</button>
          <button className="btn-ghost sm" onClick={()=>onEdit(collab)}><Edit3 size={13}/> Edit</button>
        </div>
      </div>
      <div className="share-link-box">
        <div className="share-link-label"><ExternalLink size={12}/> Public share link</div>
        <div className="share-link-url">{shareUrl}</div>
        <button className="share-link-copy" onClick={copyShare}>{linkCopied?'Copied!':'Copy'}</button>
      </div>
      <div className="detail-prog-card">
        <div className="detail-prog-header"><span className="detail-prog-stage">{s.label}</span><span className="detail-prog-pct" style={{color:barColor}}>{pct}%</span></div>
        <div className="detail-prog-track"><div className="detail-prog-fill" style={{width:`${pct}%`,background:barColor}}/></div>
        <div className="prog-dots">{STAGES.map((st,i)=>(
          <button key={i} className={`prog-dot ${i<collab.stage?'done':''} ${i===collab.stage?'cur':''}`}
            style={i===collab.stage?{background:st.color,borderColor:st.color,boxShadow:`0 0 0 3px ${st.color}33`}:i<collab.stage?{background:'#10B981',borderColor:'#10B981'}:{}}
            onClick={()=>onStageChange(collab.id,i)} title={st.label}/>
        ))}</div>
        <div className="prog-labels"><span>In talks</span><span>Paid / Done</span></div>
      </div>
      {bd&&<div className={`dl-alert dl-${bd.type}`}><Timer size={15}/><div><div className="dl-title">Brand requires delivery within {collab.brandDeadlineDays} days of receiving product</div><div className="dl-sub">{bd.label} — deliver by <strong>{bd.deadline}</strong></div></div></div>}
      {collab.needsApproval&&(
        <div className="approval-info-card">
          <div className="approval-card-title"><CheckCircle size={14}/> Approval required</div>
          <div className="fg2">
            {collab.approvalDueDate&&<div><span className="detail-field-label">Approval video due</span><div className="detail-field-val">{collab.approvalDueDate} <DueBadge dateStr={collab.approvalDueDate}/></div></div>}
            {collab.approvalVideoLink&&<div><span className="detail-field-label">Video link</span><a className="detail-link" href={collab.approvalVideoLink} target="_blank" rel="noreferrer">{collab.approvalVideoLink}</a></div>}
          </div>
        </div>
      )}
      <div className="stage-track">{STAGES.map((st,i)=>(
        <button key={i} className={`stage-pill ${i<collab.stage?'done':i===collab.stage?'active':'future'}`}
          onClick={()=>onStageChange(collab.id,i)}
          style={i===collab.stage?{background:st.bg,color:st.color,borderColor:st.color}:{}}>
          {i<collab.stage&&<Check size={9}/>}{st.label}
        </button>
      ))}</div>
      <div className="detail-grid">
        <div className="detail-card">
          <div className="detail-card-title"><DollarSign size={12}/> Deal</div>
          {collab.value&&<Row l="Value"><strong>${parseFloat(collab.value).toLocaleString()} AUD</strong></Row>}
          <Row l="Type"><TypeBadge type={collab.type} small/></Row>
          <Row l="Priority"><PriorityBadge priority={collab.priority||'normal'} small/></Row>
        </div>
        <div className="detail-card">
          <div className="detail-card-title"><Clock size={12}/> Dates</div>
          {collab.due&&<Row l="Content due"><strong>{collab.due}</strong></Row>}
          {collab.live&&<Row l="Go-live"><strong>{collab.live}</strong></Row>}
          {collab.due&&<Row l="Due in"><DueBadge dateStr={collab.due}/></Row>}
          {bd&&<Row l="Brand timer"><BrandDlBadge collab={collab}/></Row>}
        </div>
        <div className="detail-card">
          <div className="detail-card-title"><Camera size={12}/> Logistics</div>
          <Row l="Shoot"><strong>{SHOOT_LOCATIONS.find(s=>s.id===collab.shoot)?.label||'—'}</strong></Row>
          {collab.shootAddress&&<Row l="Address"><strong>{collab.shootAddress}</strong></Row>}
          {collab.shootDate&&<Row l="Appt."><strong>{collab.shootDate}{collab.shootTime&&` at ${collab.shootTime}`}</strong></Row>}
          {collab.shootDate&&<Row l="Shoot">{collab.shootDone?<span className="due-chip ok"><Check size={10}/> Done</span>:<span className="due-chip warn">Pending</span>}</Row>}
          <Row l="Product"><strong>{DELIVERY_STATUS.find(d=>d.id===collab.delivery)?.label||'—'}</strong></Row>
          {collab.productReceivedDate&&<Row l="Received"><strong>{collab.productReceivedDate}</strong></Row>}
          {collab.contact&&<Row l="Contact"><strong>{collab.contact}</strong></Row>}
          {collab.email&&<Row l="Email"><a className="detail-link" href={`mailto:${collab.email}`}>{collab.email}</a></Row>}
        </div>
        {preShootItems.length>0&&(
          <div className="detail-card preshoot-card">
            <div className="detail-card-title"><AlertTriangle size={12}/> Pre-shoot requirements</div>
            <ul className="delivs-list">{preShootItems.map((d,i)=><li key={i} className={d.done?'done':''}><div className="chk-circle">{d.done&&<Check size={9}/>}</div><div><div>{d.item}</div>{d.notes&&<div className="deliv-notes-text">{d.notes}</div>}</div></li>)}</ul>
          </div>
        )}
        {regularItems.length>0&&(
          <div className="detail-card">
            <div className="detail-card-title"><Package size={12}/> Deliverables</div>
            <ul className="delivs-list">{regularItems.map((d,i)=><li key={i} className={d.done?'done':''}><div className="chk-circle">{d.done&&<Check size={9}/>}</div><div><div>{d.item}</div>{d.notes&&<div className="deliv-notes-text">{d.notes}</div>}</div></li>)}</ul>
          </div>
        )}
      </div>
      {collab.notes&&<div className="detail-notes"><div className="detail-card-title"><Edit3 size={12}/> Notes</div><p>{collab.notes}</p></div>}
      {(collab.affiliateCode||collab.affiliateLink)&&(
        <div className="detail-notes affiliate-card">
          <div className="detail-card-title"><ExternalLink size={12}/> Affiliate / coupon</div>
          {collab.affiliateCode&&<div className="affiliate-row"><span className="affiliate-label">Coupon code</span><div className="affiliate-code-wrap"><span className="affiliate-code">{collab.affiliateCode}</span><button className="share-link-copy" onClick={()=>navigator.clipboard.writeText(collab.affiliateCode)}>Copy</button></div></div>}
          {collab.affiliateLink&&<div className="affiliate-row" style={{marginTop:8}}><span className="affiliate-label">Affiliate link</span><div className="affiliate-code-wrap"><a className="detail-link" href={collab.affiliateLink} target="_blank" rel="noreferrer">{collab.affiliateLink}</a><button className="share-link-copy" onClick={()=>navigator.clipboard.writeText(collab.affiliateLink)}>Copy</button></div></div>}
        </div>
      )}
      {collab.createdAt&&<div className="detail-ts">Added {new Date(collab.createdAt).toLocaleDateString('en-AU')}{collab.updatedAt&&` · Updated ${new Date(collab.updatedAt).toLocaleDateString('en-AU')}`}</div>}
    </div>
  );
}

// ─── SHARE PAGE (public) ──────────────────────────────────────────────────────
function SharePage() {
  const { shareId } = useParams();
  const [collab, setCollab] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getCollabByShareId(shareId).then(c=>{ setCollab(c); setLoading(false); }); }, [shareId]);
  if (loading) return <div className="share-loading"><div className="share-spinner"/></div>;
  if (!collab) return <div className="share-notfound"><div className="share-notfound-icon">✦</div><h2>Not found</h2><p>This link may have been removed.</p></div>;
  const pct = stagePct(collab.stage);
  const s = STAGES[collab.stage]||STAGES[0];
  const t = COLLAB_TYPES.find(x=>x.id===collab.type)||COLLAB_TYPES[0];
  const barColor = pct===100?'#059669':pct>=66?'#10B981':'#C9A84C';
  const regularItems = (collab.deliverables||[]).filter(d=>!d.preShoot);
  return (
    <div className="share-page">
      <div className="share-blobs"><div className="sb1"/><div className="sb2"/></div>
      <div className="share-container">
        <div className="share-brand-header">
          <div className="share-creator">A collaboration with <strong>@{collab.creatorHandle||collab.handle||'creator'}</strong></div>
          <Avatar name={collab.brand} size={64}/>
          <h1 className="share-brand-name">{collab.brand}</h1>
          <div className="share-badges"><Badge bg={t.bg} color={t.color} label={t.label}/><span className="share-cat">{collab.cat}</span></div>
        </div>
        <div className="share-progress-card">
          <div className="share-prog-header"><span>Progress</span><span style={{color:barColor,fontWeight:600}}>{pct}%</span></div>
          <div className="share-prog-track"><div className="share-prog-fill" style={{width:`${pct}%`,background:barColor}}/></div>
          <div className="share-stage-label" style={{color:s.color,background:s.bg}}>{s.label}</div>
        </div>
        <div className="share-info-grid">
          {collab.due&&<div className="share-info-card"><div className="share-info-label"><Clock size={12}/> Content due</div><div className="share-info-val">{collab.due}</div></div>}
          {collab.live&&<div className="share-info-card"><div className="share-info-label"><ExternalLink size={12}/> Go-live</div><div className="share-info-val">{collab.live}</div></div>}
          {collab.shoot&&<div className="share-info-card"><div className="share-info-label"><Camera size={12}/> Shoot</div><div className="share-info-val">{SHOOT_LOCATIONS.find(s=>s.id===collab.shoot)?.label}{collab.shootDate&&<div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>{collab.shootDate}{collab.shootTime&&` at ${collab.shootTime}`}</div>}{collab.shootAddress&&<div style={{fontSize:11,color:'var(--text3)',marginTop:1}}>{collab.shootAddress}</div>}</div></div>}
        </div>
        {collab.needsApproval&&<div className="share-approval-box"><CheckCircle size={14}/> <strong>Approval required</strong>{collab.approvalDueDate&&<span> — approval video due {collab.approvalDueDate}</span>}{collab.approvalVideoLink&&<div style={{marginTop:6}}><a href={collab.approvalVideoLink} target="_blank" rel="noreferrer" style={{color:'#C9A84C'}}>View approval video →</a></div>}</div>}
        {regularItems.length>0&&<div className="share-deliverables"><div className="share-section-title">Deliverables</div><ul className="share-deliv-list">{regularItems.map((d,i)=><li key={i} className={d.done?'done':''}><div className="share-check">{d.done&&<Check size={10}/>}</div><div><div>{d.item}</div>{d.notes&&<div style={{fontSize:11,color:'#B0A0CC',marginTop:2}}>{d.notes}</div>}</div></li>)}</ul></div>}
        {collab.notes&&<div className="share-notes"><div className="share-section-title">Notes</div><p>{collab.notes}</p></div>}
        {(collab.affiliateCode||collab.affiliateLink)&&<div className="share-affiliate"><div className="share-section-title">Affiliate / coupon</div>{collab.affiliateCode&&<div className="share-affiliate-row"><span>Coupon code</span><strong>{collab.affiliateCode}</strong></div>}{collab.affiliateLink&&<div className="share-affiliate-row"><span>Affiliate link</span><a href={collab.affiliateLink} target="_blank" rel="noreferrer" style={{color:'var(--p-dark)',wordBreak:'break-all'}}>{collab.affiliateLink}</a></div>}</div>}
        <div className="share-footer">My Creator Kit ✦</div>
      </div>
    </div>
  );
}

// ─── CHANGE PASSWORD MODAL ────────────────────────────────────────────────────
function ChangePwModal({ onClose }) {
  const [cur,setCur]=useState('');const [nw,setNw]=useState('');const [conf,setConf]=useState('');
  const [err,setErr]=useState('');const [ok,setOk]=useState(false);
  const handle=async()=>{
    if(nw!==conf){setErr("Passwords don't match");return;}
    if(nw.length<4){setErr('Min 4 characters');return;}
    const res=await apiChangePassword(cur,nw);
    if(res.ok)setOk(true);else setErr(res.message||'Error');
  };
  return(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" style={{maxWidth:380}}>
        <div className="modal-hdr"><h2>Change password</h2><button className="icon-btn" onClick={onClose}><X size={17}/></button></div>
        <div className="modal-body">
          {ok?<div className="success-msg"><Check size={18}/> Password updated!</div>:<>
            <Field label="Current password"><input type="password" value={cur} onChange={e=>setCur(e.target.value)}/></Field>
            <Field label="New password" style={{marginTop:10}}><input type="password" value={nw} onChange={e=>setNw(e.target.value)}/></Field>
            <Field label="Confirm" style={{marginTop:10}}><input type="password" value={conf} onChange={e=>setConf(e.target.value)}/></Field>
            {err&&<div className="login-err" style={{marginTop:10}}>{err}</div>}
          </>}
        </div>
        <div className="modal-ftr"><div style={{flex:1}}/><button className="btn-ghost" onClick={onClose}>Close</button>{!ok&&<button className="btn-primary" onClick={handle}>Update</button>}</div>
      </div>
    </div>
  );
}

// ─── CALENDAR PAGE ────────────────────────────────────────────────────────────
function CalendarPage({ collabs, onMarkDone, onMarkShootDone, onOpen }) {
  const [sort, setSort] = useState('date');
  const [showDone, setShowDone] = useState(false);
  const today = new Date(); today.setHours(0,0,0,0);
  const events = [];
  collabs.forEach(c => {
    if (c.shootDate && (c.shoot==='brand'||c.shoot==='both')) events.push({ collabId:c.id, collab:c, type:'shoot', label:'Shoot appointment', date:c.shootDate, time:c.shootTime||'', address:c.shootAddress||'', brand:c.brand, priority:c.priority||'normal', done:c.shootDone||false });
    if (c.due) events.push({ collabId:c.id, collab:c, type:'due', label:'Content due', date:c.due, time:'', address:'', brand:c.brand, priority:c.priority||'normal', done:c.stage>=7 });
    if (c.live) events.push({ collabId:c.id, collab:c, type:'live', label:'Go-live date', date:c.live, time:'', address:'', brand:c.brand, priority:c.priority||'normal', done:c.stage>=8 });
    if (c.approvalDueDate&&c.needsApproval) events.push({ collabId:c.id, collab:c, type:'approval', label:'Approval video due', date:c.approvalDueDate, time:'', address:'', brand:c.brand, priority:c.priority||'normal', done:c.stage>=6 });
  });
  const filtered = events.filter(e => showDone ? true : !e.done);
  const sorted = [...filtered].sort((a,b) => {
    if (sort==='date') return new Date(a.date)-new Date(b.date);
    if (sort==='priority') { const o={urgent:0,high:1,normal:2,low:3}; return (o[a.priority]??2)-(o[b.priority]??2); }
    if (sort==='brand') return a.brand.localeCompare(b.brand);
    return 0;
  });
  const grouped = {};
  sorted.forEach(e => {
    const d = new Date(e.date+'T00:00:00');
    const key = d.toLocaleString('default', { month:'long', year:'numeric' });
    if (!grouped[key]) grouped[key]=[];
    grouped[key].push(e);
  });
  const typeConfig = { shoot:{color:'#EC4899',bg:'#FDF2F8',icon:'📍'}, due:{color:'#C9A84C',bg:'#FAF5E8',icon:'📋'}, live:{color:'#10B981',bg:'#D1FAE5',icon:'🚀'}, approval:{color:'#F59E0B',bg:'#FFFBEB',icon:'✅'} };
  const getDayLabel = (dateStr) => {
    const d = new Date(dateStr+'T00:00:00');
    const diff = Math.round((d-today)/86400000);
    if (diff===0) return { label:'Today', cls:'day-today' };
    if (diff===1) return { label:'Tomorrow', cls:'day-soon' };
    if (diff<0) return { label:`${Math.abs(diff)}d ago`, cls:'day-past' };
    if (diff<=7) return { label:`In ${diff}d`, cls:'day-soon' };
    return { label:d.toLocaleDateString('en-AU',{weekday:'short',day:'numeric',month:'short'}), cls:'day-future' };
  };
  const upcoming = events.filter(e=>!e.done&&new Date(e.date+'T00:00:00')>=today).length;
  const overdue = events.filter(e=>!e.done&&new Date(e.date+'T00:00:00')<today).length;
  return (
    <div className="page-content">
      <div className="page-header">
        <h1 className="page-title">Calendar{upcoming>0&&<span className="page-count">{upcoming} upcoming</span>}{overdue>0&&<span className="page-count" style={{background:'#FEE2E2',color:'#991B1B'}}>{overdue} overdue</span>}</h1>
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <label className="cal-toggle-label"><input type="checkbox" checked={showDone} onChange={e=>setShowDone(e.target.checked)}/><span className="toggle-slider"/><span>Show completed</span></label>
          <select className="filter-sel" value={sort} onChange={e=>setSort(e.target.value)}><option value="date">Sort by date</option><option value="priority">Sort by priority</option><option value="brand">Sort by brand</option></select>
        </div>
      </div>
      <div className="cal-legend">{Object.entries(typeConfig).map(([k,v])=><span key={k} className="cal-legend-item" style={{background:v.bg,color:v.color}}>{v.icon} {k==='shoot'?'Shoot':k==='due'?'Content due':k==='live'?'Go-live':'Approval'}</span>)}</div>
      {sorted.length===0&&<div className="empty-state"><Calendar size={28}/><p>{showDone?'No events at all.':'No upcoming events — all done!'}</p></div>}
      {Object.entries(grouped).map(([month,items])=>(
        <div key={month} className="cal-month-group">
          <div className="cal-month-label">{month}</div>
          <div className="cal-events">{items.map((e,i)=>{
            const tc=typeConfig[e.type]; const dl=getDayLabel(e.date);
            const p=PRIORITIES.find(x=>x.id===e.priority)||PRIORITIES[2];
            const d=new Date(e.date+'T00:00:00');
            return <div key={i} className={`cal-event-row ${e.done?'done':''}`}>
              <div className="cal-date-col"><div className="cal-day-num">{d.getDate()}</div><div className="cal-day-name">{d.toLocaleString('default',{weekday:'short'})}</div></div>
              <div className="cal-event-dot" style={{background:tc.color}}/>
              <div className="cal-event-body" onClick={()=>onOpen(e.collab)} style={{cursor:'pointer'}}>
                <div className="cal-event-top"><span className="cal-event-type" style={{background:tc.bg,color:tc.color}}>{tc.icon} {e.label}</span><span className={`cal-day-badge ${dl.cls}`}>{dl.label}</span>{e.priority!=='normal'&&<span className="badge sm" style={{background:p.bg,color:p.color}}>{p.label}</span>}</div>
                <div className="cal-event-brand">{e.brand}</div>
                {e.time&&<div className="cal-event-meta"><Clock size={11}/> {e.time}</div>}
                {e.address&&<div className="cal-event-meta"><MapPin size={11}/> {e.address}</div>}
              </div>
              {e.done?<span className="cal-done-tag"><Check size={12}/> Done</span>:(
                <button className="cal-mark-done-btn" onClick={()=>{
                  if(e.type==='shoot') onMarkShootDone(e.collabId);
                  else if(e.type==='due') onMarkDone(e.collabId,7);
                  else if(e.type==='live') onMarkDone(e.collabId,8);
                  else if(e.type==='approval') onMarkDone(e.collabId,6);
                }}><Check size={13}/> Mark done</button>
              )}
            </div>;
          })}</div>
        </div>
      ))}
    </div>
  );
}


// ─── THEME PICKER ─────────────────────────────────────────────────────────────
function ThemePicker({ onSelect, current = null, isModal = false }) {
  const [hovered, setHovered] = useState(current || 'violet');
  const preview = THEMES.find(t => t.id === hovered) || THEMES[0];

  return (
    <div className={`theme-picker-wrap ${isModal ? 'theme-picker-modal' : 'theme-picker-fullscreen'}`}>
      {!isModal && (
        <div className="theme-picker-hero">
          <div className="theme-picker-logo"><Sparkles size={28}/></div>
          <h1 className="theme-picker-title">Welcome to My Creator Kit</h1>
          <p className="theme-picker-sub">Choose your theme to get started — you can always change it later in Settings.</p>
        </div>
      )}
      {isModal && <div className="theme-picker-modal-title"><Palette size={15}/> Choose theme</div>}

      {/* Live preview strip */}
      <div className="theme-preview-bar" style={{
        background: `linear-gradient(135deg, ${preview.vars['--sidebar-from']}, ${preview.vars['--sidebar-to']})`,
        borderColor: preview.vars['--p'],
      }}>
        <div className="theme-preview-dot" style={{ background: preview.vars['--p'] }}/>
        <div style={{ flex:1 }}>
          <div className="theme-preview-name" style={{ color: preview.vars['--p'] }}>{preview.name}</div>
          <div className="theme-preview-desc">{preview.description}</div>
        </div>
        <div className="theme-preview-swatches">
          {[preview.vars['--p'], preview.vars['--p2'], preview.vars['--pink'], preview.vars['--p-light']].map((c,i) => (
            <div key={i} className="theme-swatch" style={{ background: c }}/>
          ))}
        </div>
      </div>

      {/* Theme grid */}
      <div className="theme-grid">
        {THEMES.map(t => (
          <button
            key={t.id}
            className={`theme-card ${(current || hovered) === t.id ? 'selected' : ''}`}
            onMouseEnter={() => setHovered(t.id)}
            onClick={() => onSelect(t.id)}
            style={{ '--tc': t.preview }}
          >
            <div className="theme-card-swatch" style={{
              background: `linear-gradient(135deg, ${t.vars['--sidebar-from']}, ${t.vars['--p']})`,
            }}>
              <div className="theme-card-dot" style={{ background: t.vars['--p-light'] }}/>
            </div>
            <div className="theme-card-name">{t.name}</div>
            {(current || hovered) === t.id && <div className="theme-card-check"><Check size={11}/></div>}
          </button>
        ))}
      </div>

      {!isModal && (
        <button className="theme-picker-continue" style={{
          background: `linear-gradient(135deg, ${preview.vars['--p']}, ${preview.vars['--p2']})`,
        }} onClick={() => onSelect(hovered)}>
          Continue with {preview.name} →
        </button>
      )}
    </div>
  );
}

// ─── HOW TO USE PAGE ──────────────────────────────────────────────────────────
function HowToUsePage() {
  const [active, setActive] = useState('collabs');
  const section = HOW_TO_USE.find(s => s.id === active) || HOW_TO_USE[0];

  return (
    <div className="page-content htu-page">
      <div className="page-header">
        <h1 className="page-title"><BookOpen size={20}/> How to use</h1>
      </div>
      <div className="htu-layout">
        {/* Sidebar nav */}
        <div className="htu-nav">
          {HOW_TO_USE.map(s => (
            <button
              key={s.id}
              className={`htu-nav-item ${active === s.id ? 'active' : ''}`}
              onClick={() => setActive(s.id)}
            >
              <span className="htu-nav-icon">{s.icon}</span>
              <div>
                <div className="htu-nav-title">{s.title}</div>
                <div className="htu-nav-sub">{s.subtitle}</div>
              </div>
              <ChevronRight size={13} className="htu-nav-arrow"/>
            </button>
          ))}
        </div>
        {/* Content */}
        <div className="htu-content">
          <div className="htu-content-header">
            <div className="htu-content-icon">{section.icon}</div>
            <div>
              <h2 className="htu-content-title">{section.title}</h2>
              <p className="htu-content-sub">{section.subtitle}</p>
            </div>
          </div>
          <div className="htu-steps">
            {section.steps.map((step, i) => (
              <div key={i} className="htu-step">
                <div className="htu-step-num">{i + 1}</div>
                <div className="htu-step-body">
                  <div className="htu-step-title">{step.title}</div>
                  <div className="htu-step-desc">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="htu-tip-box">
            <Star size={13}/>
            <span>Tip — your data auto-saves every time you make a change. No save button needed for most things.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── WELCOME POPUP (first time) ───────────────────────────────────────────────
function WelcomePopup({ onClose }) {
  const [step, setStep] = useState(0);
  const steps = HOW_TO_USE;
  const isLast = step === steps.length - 1;

  return (
    <div className="modal-overlay" style={{ zIndex: 500 }}>
      <div className="welcome-popup">
        <div className="welcome-popup-header">
          <div className="welcome-popup-logo"><Sparkles size={18}/> My Creator Kit</div>
          <div className="welcome-popup-steps">{step + 1} / {steps.length}</div>
        </div>
        <div className="welcome-popup-body">
          <div className="welcome-popup-icon">{steps[step].icon}</div>
          <h2 className="welcome-popup-title">{steps[step].title}</h2>
          <p className="welcome-popup-sub">{steps[step].subtitle}</p>
          <div className="welcome-popup-points">
            {steps[step].steps.slice(0, 3).map((s, i) => (
              <div key={i} className="welcome-popup-point">
                <Check size={12}/>
                <span><strong>{s.title}</strong> — {s.desc.slice(0, 80)}…</span>
              </div>
            ))}
          </div>
        </div>
        <div className="welcome-popup-dots">
          {steps.map((_, i) => (
            <div key={i} className={`welcome-dot ${i === step ? 'active' : i < step ? 'done' : ''}`} onClick={() => setStep(i)}/>
          ))}
        </div>
        <div className="welcome-popup-footer">
          {step > 0 && <button className="btn-ghost sm" onClick={() => setStep(s => s - 1)}><ChevronLeft size={13}/> Back</button>}
          <div style={{ flex: 1 }}/>
          <button className="btn-ghost sm" onClick={onClose}>Skip tour</button>
          {isLast ? (
            <button className="btn-primary sm" onClick={onClose}><Check size={13}/> Let's go!</button>
          ) : (
            <button className="btn-primary sm" onClick={() => setStep(s => s + 1)}>Next <ChevronRight size={13}/></button>
          )}
        </div>
      </div>
    </div>
  );
}



// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ collabs, invoices, profile, onAddCollab, onAddInvoice, onOpenCollab, onNavigate }) {
  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();
  const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
  const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

  // ── Earnings ──────────────────────────────────────────────────────────────
  const getInvTotal = (inv) =>
    inv.paymentType === 'milestone'
      ? inv.milestones?.reduce((a,m) => a + (parseFloat(m.amount||0)), 0) || 0
      : inv.items?.reduce((a,i) => a + (parseFloat(i.amount||0) * parseFloat(i.qty||1)), 0) || 0;

  const earnedThisMonth = invoices
    .filter(inv => {
      const d = new Date(inv.invoiceDate);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear &&
        (inv.alreadyPaid || inv.paymentType === 'milestone' && inv.milestones?.every(m => m.paid));
    })
    .reduce((a, inv) => a + getInvTotal(inv), 0);

  const earnedLastMonth = invoices
    .filter(inv => {
      const d = new Date(inv.invoiceDate);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear &&
        (inv.alreadyPaid || inv.paymentType === 'milestone' && inv.milestones?.every(m => m.paid));
    })
    .reduce((a, inv) => a + getInvTotal(inv), 0);

  const pendingIncome = invoices
    .filter(inv => !inv.alreadyPaid && !(inv.paymentType === 'milestone' && inv.milestones?.every(m => m.paid)))
    .reduce((a, inv) => a + getInvTotal(inv), 0);

  const monthChange = earnedLastMonth > 0
    ? Math.round(((earnedThisMonth - earnedLastMonth) / earnedLastMonth) * 100)
    : null;

  // ── Active collabs ────────────────────────────────────────────────────────
  const activeCollabs = collabs.filter(c => c.stage > 0 && c.stage < STAGES.length - 1);
  const urgentCollabs = collabs.filter(c => effPriority(c) === 'urgent');
  const overdueCollabs = collabs.filter(c =>
    dueInfo(c.due)?.type === 'over' || brandDeadlineInfo(c)?.type === 'over'
  );

  // ── Upcoming deadlines (next 7 days) ──────────────────────────────────────
  const today = new Date(); today.setHours(0,0,0,0);
  const in7 = new Date(today); in7.setDate(today.getDate() + 7);

  const upcomingEvents = [];
  collabs.forEach(c => {
    const check = (dateStr, label, type) => {
      if (!dateStr) return;
      const d = new Date(dateStr + 'T00:00:00');
      if (d >= today && d <= in7) {
        upcomingEvents.push({ collab: c, date: d, dateStr, label, type });
      }
    };
    check(c.due, 'Content due', 'due');
    check(c.live, 'Go-live', 'live');
    check(c.approvalDueDate, 'Approval due', 'approval');
    if (c.shootDate && (c.shoot === 'brand' || c.shoot === 'both')) {
      check(c.shootDate, 'Shoot', 'shoot');
    }
  });
  upcomingEvents.sort((a, b) => a.date - b.date);

  const typeColors = {
    due:      { color: '#C9A84C', bg: '#FAF5E8', icon: '📋' },
    live:     { color: '#10B981', bg: '#D1FAE5', icon: '🚀' },
    approval: { color: '#F59E0B', bg: '#FFFBEB', icon: '✅' },
    shoot:    { color: '#EC4899', bg: '#FDF2F8', icon: '📍' },
  };

  const getDayLabel = (d) => {
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return { label: 'Today', cls: 'day-today' };
    if (diff === 1) return { label: 'Tomorrow', cls: 'day-soon' };
    return { label: `In ${diff}d`, cls: 'day-soon' };
  };

  const monthName = now.toLocaleString('default', { month: 'long' });
  const greeting = () => {
    const h = now.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="page-content dashboard">
      {/* Header */}
      <div className="dash-header">
        <div>
          <div className="dash-greeting">{greeting()}, {profile?.displayName?.split(' ')[0] || profile?.instagramHandle} ✦</div>
          <div className="dash-sub">Here's your creator business at a glance</div>
        </div>
        <div className="dash-quick-actions">
          <button className="btn-primary" onClick={onAddCollab}><Plus size={14}/> Add collab</button>
          <button className="btn-ghost" onClick={onAddInvoice}><Receipt size={14}/> New invoice</button>
        </div>
      </div>

      {/* Stats row */}
      <div className="dash-stats">
        <div className="dash-stat-card">
          <div className="dash-stat-label">Earned in {monthName}</div>
          <div className="dash-stat-val">${earnedThisMonth.toLocaleString('en-AU', {minimumFractionDigits:0})}</div>
          {monthChange !== null && (
            <div className={`dash-stat-change ${monthChange >= 0 ? 'up' : 'down'}`}>
              {monthChange >= 0 ? '↑' : '↓'} {Math.abs(monthChange)}% vs last month
            </div>
          )}
          {monthChange === null && earnedLastMonth === 0 && (
            <div className="dash-stat-change neutral">First month tracked</div>
          )}
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">Pending payment</div>
          <div className="dash-stat-val" style={{color: pendingIncome > 0 ? 'var(--p-dark)' : 'var(--text3)'}}>
            ${pendingIncome.toLocaleString('en-AU', {minimumFractionDigits:0})}
          </div>
          <div className="dash-stat-change neutral">{invoices.filter(i => !i.alreadyPaid).length} unpaid invoice{invoices.filter(i=>!i.alreadyPaid).length!==1?'s':''}</div>
        </div>
        <div className="dash-stat-card" onClick={() => onNavigate('kanban')} style={{cursor:'pointer'}}>
          <div className="dash-stat-label">Active collabs</div>
          <div className="dash-stat-val">{activeCollabs.length}</div>
          <div className="dash-stat-change neutral">{collabs.length} total</div>
        </div>
        <div className="dash-stat-card" style={{borderColor: urgentCollabs.length > 0 ? '#FCA5A5' : 'var(--border)'}}>
          <div className="dash-stat-label">Needs attention</div>
          <div className="dash-stat-val" style={{color: urgentCollabs.length > 0 ? '#DC2626' : 'var(--text3)'}}>
            {urgentCollabs.length + overdueCollabs.length}
          </div>
          <div className="dash-stat-change neutral" style={{color: urgentCollabs.length > 0 ? '#EF4444' : 'var(--text3)'}}>
            {urgentCollabs.length > 0 ? `${urgentCollabs.length} urgent` : overdueCollabs.length > 0 ? `${overdueCollabs.length} overdue` : 'All on track ✓'}
          </div>
        </div>
      </div>

      <div className="dash-grid">
        {/* Upcoming deadlines */}
        <div className="dash-section">
          <div className="dash-section-title">
            <Calendar size={14}/> Upcoming — next 7 days
            <span className="page-count" style={{marginLeft:6}}>{upcomingEvents.length}</span>
          </div>
          {upcomingEvents.length === 0 ? (
            <div className="dash-empty"><CheckCircle size={20} style={{color:'#10B981'}}/><span>Nothing due this week 🎉</span></div>
          ) : (
            <div className="dash-deadlines">
              {upcomingEvents.map((ev, i) => {
                const tc = typeColors[ev.type];
                const dl = getDayLabel(ev.date);
                return (
                  <div key={i} className="dash-deadline-row" onClick={() => onOpenCollab(ev.collab)}>
                    <div className="dash-deadline-dot" style={{background: tc.color}}/>
                    <div className="dash-deadline-body">
                      <div className="dash-deadline-brand">{ev.collab.brand}</div>
                      <div className="dash-deadline-label">
                        <span style={{background:tc.bg,color:tc.color}} className="badge sm">{tc.icon} {ev.label}</span>
                      </div>
                    </div>
                    <span className={`cal-day-badge ${dl.cls}`}>{dl.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Active collabs with urgency */}
        <div className="dash-section">
          <div className="dash-section-title">
            <Zap size={14}/> Active collabs
            {urgentCollabs.length > 0 && (
              <span className="badge sm" style={{background:'#FEE2E2',color:'#DC2626',marginLeft:6}}>{urgentCollabs.length} urgent</span>
            )}
          </div>
          {activeCollabs.length === 0 ? (
            <div className="dash-empty"><Sparkles size={20}/><span>No active collabs — add one!</span></div>
          ) : (
            <div className="dash-collabs">
              {[...activeCollabs]
                .sort((a,b) => {
                  const o = {urgent:0,high:1,normal:2,low:3};
                  return (o[effPriority(a)]??2) - (o[effPriority(b)]??2);
                })
                .slice(0, 6)
                .map(c => {
                  const ep = effPriority(c);
                  const s = STAGES[c.stage] || STAGES[0];
                  const bd = brandDeadlineInfo(c);
                  const urgColor = {urgent:'#EF4444',high:'#F59E0B'}[ep];
                  return (
                    <div key={c.id} className="dash-collab-row" onClick={() => onOpenCollab(c)}
                      style={urgColor ? {borderLeft:`3px solid ${urgColor}`} : {}}>
                      <Avatar name={c.brand} size={30}/>
                      <div className="dash-collab-info">
                        <div className="dash-collab-brand">{c.brand}</div>
                        <div className="dash-collab-stage">
                          <span className="badge sm" style={{background:s.bg,color:s.color}}>{s.label}</span>
                          {bd && <BrandDlBadge collab={c}/>}
                          {!bd && c.due && <DueBadge dateStr={c.due}/>}
                        </div>
                      </div>
                      <div className="dash-collab-progress">
                        <ProgressBar stage={c.stage} collab={c} showLabel/>
                      </div>
                      <ChevronRight size={13} style={{color:'var(--text3)',flexShrink:0}}/>
                    </div>
                  );
                })}
              {activeCollabs.length > 6 && (
                <button className="btn-ghost sm" style={{marginTop:6,width:'100%'}} onClick={() => onNavigate('list')}>
                  View all {activeCollabs.length} collabs
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN CRM APP ─────────────────────────────────────────────────────────────
function CRMApp({ user, profile: initialProfile }) {
  const [profile, setProfile] = useState(initialProfile);
  const [collabs, setCollabs] = useState([]);
  const [dashInvoices, setDashInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [welcomeBanner, setWelcomeBanner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [view, setView] = useState('home');
  const [modal, setModal] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [search, setSearch] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false); // false | 'firsttime' | 'settings'
  const [showWelcome, setShowWelcome] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(initialProfile?.theme || 'violet');
  const saveTimer = useRef(null);
  const lastActiveTimer = useRef(null);
  const isAdmin = user.email === ADMIN_EMAIL;

  // Apply theme on mount and when changed
  useEffect(() => {
    const themeId = initialProfile?.theme || 'violet';
    applyTheme(themeId);
    setCurrentTheme(themeId);
    // First time user — no theme set yet → show theme picker then welcome
    if (!initialProfile?.theme) {
      setShowThemePicker('firsttime');
    } else if (!initialProfile?.seenWelcome) {
      setShowWelcome(true);
      import('./storage').then(({ updateUserProfile }) => {
        updateUserProfile(user.uid, { seenWelcome: true });
      });
    }
  }, []);

  // Track last active every 5 minutes
  useEffect(() => {
    lastActiveTimer.current = setInterval(() => {
      updateLastActive(user.uid);
    }, 5 * 60 * 1000);
    return () => clearInterval(lastActiveTimer.current);
  }, [user.uid]);

  const handleThemeSelect = async (themeId) => {
    applyTheme(themeId);
    setCurrentTheme(themeId);
    await saveUserTheme(user.uid, themeId);
    const wasFirstTime = showThemePicker === 'firsttime';
    setShowThemePicker(false);
    // Only show welcome tour if this was first-time theme selection, not from Settings
    if (wasFirstTime) {
      setShowWelcome(true);
      updateUserProfile(user.uid, { seenWelcome: true });
    }
  };

  useEffect(() => {
    loadCollabs(user.uid).then(data => { setCollabs(data); setLoading(false); });
    loadInvoices(user.uid).then(data => setDashInvoices(data));
    // Check if this is a freshly-approved user
    import('firebase/firestore').then(({ getDoc, doc, deleteDoc }) => {
      import('./firebase').then(({ db }) => {
        getDoc(doc(db, 'notifications', user.uid)).then(snap => {
          if (snap.exists() && snap.data().type === 'approved') {
            setWelcomeBanner(true);
            deleteDoc(doc(db, 'notifications', user.uid));
          }
        });
      });
    });
  }, [user.uid]);

  const persistCollab = useCallback(async (collab) => {
    setSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await saveCollab(user.uid, collab, profile?.instagramHandle);
      setLastSaved(new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}));
      setSaving(false);
    }, 600);
  }, [user.uid, profile?.instagramHandle]);

  const handleSave = (c) => {
    setCollabs(prev => prev.find(x=>x.id===c.id) ? prev.map(x=>x.id===c.id?c:x) : [...prev,c]);
    persistCollab(c);
    setModal(null);
    if (detail?.id===c.id) setDetail(c);
  };
  const handleDelete = async (id) => {
    await deleteCollab(user.uid, id);
    setCollabs(prev=>prev.filter(x=>x.id!==id));
    setModal(null); setDetail(null);
  };
  const handleStage = (id, stage) => {
    const now = new Date().toISOString();
    setCollabs(prev => {
      const next = prev.map(x=>x.id===id?{...x,stage,updatedAt:now}:x);
      const updated = next.find(x=>x.id===id);
      if (updated) persistCollab(updated);
      return next;
    });
    if (detail?.id===id) setDetail(d=>({...d,stage,updatedAt:now}));
  };
  const handleShootDone = (id) => {
    const now = new Date().toISOString();
    setCollabs(prev => {
      const next = prev.map(x=>x.id===id?{...x,shootDone:true,updatedAt:now}:x);
      const updated = next.find(x=>x.id===id);
      if (updated) persistCollab(updated);
      return next;
    });
    if (detail?.id===id) setDetail(d=>({...d,shootDone:true,updatedAt:now}));
  };

  const pOrder = { urgent:0, high:1, normal:2, low:3 };
  const filtered = collabs
    .filter(c => {
      if (filterType && c.type!==filterType) return false;
      if (filterStage!=='' && c.stage!==parseInt(filterStage)) return false;
      if (filterPriority && (c.priority||'normal')!==filterPriority) return false;
      if (search && !c.brand.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a,b)=>(pOrder[effPriority(a)]??2)-(pOrder[effPriority(b)]??2));

  const totalVal = collabs.filter(c=>c.type==='paid'||c.type==='paidbarter').reduce((a,c)=>a+(parseFloat(c.value)||0),0);
  const active = collabs.filter(c=>c.stage>0&&c.stage<STAGES.length-1).length;
  const overdueN = collabs.filter(c=>dueInfo(c.due)?.type==='over'||brandDeadlineInfo(c)?.type==='over').length;
  const urgentN = collabs.filter(c=>effPriority(c)==='urgent').length;

  if (loading) return <div className="app-loading"><div className="app-spinner"/><span>Loading your collabs…</span></div>;

  const navItems = [
    { id:'home',      icon:<Sparkles size={14}/>,   label:'Home' },
    { id:'kanban',    icon:<LayoutGrid size={14}/>, label:'Kanban' },
    { id:'list',      icon:<List size={14}/>,       label:'All collabs' },
    { id:'calendar',  icon:<Calendar size={14}/>,   label:'Calendar' },
    { id:'campaigns', icon:<Link size={14}/>,       label:'Campaigns' },
    { id:'invoices',  icon:<Receipt size={14}/>,    label:'Invoices' },
    { id:'links',     icon:<Globe size={14}/>,      label:'My Links' },
    { id:'howto',     icon:<HelpCircle size={14}/>, label:'How to use' },
    { id:'profile',   icon:<User size={14}/>,       label:'My profile' },
    ...(isAdmin ? [{ id:'admin', icon:<Users size={14}/>, label:'Users' }] : []),
  ];

  return (
    <div className="app">
      {welcomeBanner && (
        <div className="welcome-banner">
          <div className="welcome-banner-inner">
            <span>🎉 Your account is approved — welcome to My Creator Kit!</span>
            <button onClick={() => setWelcomeBanner(false)}>✕</button>
          </div>
        </div>
      )}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <svg width="22" height="22" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="44" height="44" rx="10" fill="url(#sb-grad)"/>
            <defs>
              <linearGradient id="sb-grad" x1="0" y1="0" x2="44" y2="44" gradientUnits="userSpaceOnUse">
                <stop stopColor="var(--p)"/>
                <stop offset="1" stopColor="var(--pink)"/>
              </linearGradient>
            </defs>
            <g transform="translate(22,22)" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="-12,1 -12,13 12,13 12,1" fill="rgba(255,255,255,0.2)" stroke="white"/>
              <polyline points="-12,1 -5,-4 5,-4 12,1"/>
              <line x1="0" y1="-4" x2="0" y2="1"/>
              <rect x="-10" y="-12" width="8" height="6" rx="1.5" fill="rgba(255,255,255,0.15)" stroke="white"/>
              <circle cx="-6" cy="-9" r="1.8" fill="rgba(255,255,255,0.2)" stroke="white"/>
              <line x1="0" y1="-13" x2="0" y2="-4"/>
              <polygon points="0,-16 -2,-13 2,-13" fill="white" stroke="white" strokeWidth="0.5"/>
              <rect x="4" y="-12" width="6" height="8" rx="3" fill="rgba(255,255,255,0.15)" stroke="white"/>
              <path d="M3,-5 Q3,-1 7,-1 Q11,-1 11,-5"/>
              <line x1="7" y1="-1" x2="7" y2="1"/>
              <path d="M-3,-18 L-2.2,-15.5 L0,-14.8 L-2.2,-14 L-3,-11.5 L-3.8,-14 L-6,-14.8 L-3.8,-15.5 Z" fill="white" stroke="none"/>
              <path d="M5,4 L5.8,6.5 L8,7 L5.8,7.5 L5,10 L4.2,7.5 L2,7 L4.2,6.5 Z" fill="rgba(255,255,255,0.7)" stroke="none"/>
            </g>
          </svg>
          <span>My Creator Kit</span>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(n=>(
            <button key={n.id} className={`nav-item ${view===n.id&&!detail?'active':''}`} onClick={()=>{ setDetail(null); setView(n.id); }}>
              {n.icon}<span>{n.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-div"/>
        <div className="sidebar-stats">
          <div className="ss"><TrendingUp size={11}/><span>${totalVal.toLocaleString()} AUD</span></div>
          <div className="ss"><Camera size={11}/><span>{active} active</span></div>
          {urgentN>0&&<div className="ss urgent"><Zap size={11}/><span>{urgentN} urgent</span></div>}
          {overdueN>0&&<div className="ss overdue"><AlertCircle size={11}/><span>{overdueN} overdue</span></div>}
        </div>
        <div className="sidebar-div"/>
        <div className="sidebar-footer">
          {lastSaved&&<div className="save-ind"><Save size={10}/><span>{saving?'Saving…':`Saved ${lastSaved}`}</span></div>}
          <div className="sidebar-user"><Avatar name={profile?.displayName||profile?.instagramHandle} size={22}/><span>@{profile?.instagramHandle}</span></div>
          <button className="sidebar-action" onClick={()=>setShowThemePicker('settings')}><Palette size={12}/><span>Theme</span></button>
          <button className="sidebar-action" onClick={()=>setShowSettings(true)}><Settings size={12}/><span>Settings</span></button>
          <button className="sidebar-action" onClick={apiSignOut}><LogOut size={12}/><span>Sign out</span></button>
        </div>
      </aside>

      <main className="main">
        {view==='home' && (
          <Dashboard
            collabs={collabs}
            invoices={dashInvoices}
            profile={profile}
            onAddCollab={() => { setView('kanban'); setModal({}); }}
            onAddInvoice={() => { setView('invoices'); }}
            onOpenCollab={c => { setDetail(c); setView('kanban'); }}
            onNavigate={v => setView(v)}
          />
        )}
        {view==='profile' && <ProfilePage profile={profile} onUpdate={setProfile}/>}
        {view==='admin' && isAdmin && <AdminPanel/>}
        {view==='invoices' && <InvoicesPage uid={user.uid} profile={profile} collabs={collabs}/>}
        {view==='campaigns' && <CampaignsPage uid={user.uid} collabs={collabs}/>}
        {view==='calendar' && <CalendarPage collabs={collabs} onMarkDone={handleStage} onMarkShootDone={handleShootDone} onOpen={c=>{setDetail(c);setView('kanban');}}/>}
        {view==='links' && <LinksEditor profile={profile}/>}
        {view==='howto' && <HowToUsePage/>}
        {(view==='kanban'||view==='list') && (
          detail ? (
            <DetailView collab={detail} onBack={()=>setDetail(null)} onEdit={c=>setModal(c)} onStageChange={handleStage} onCreateInvoice={c=>setInvoiceModal({collab:c})}/>
          ) : (
            <>
              <div className="topbar">
                <div className="topbar-left"><h1 className="page-title">{view==='kanban'?'Pipeline':'All collabs'}<span className="page-count">{filtered.length}</span></h1></div>
                <div className="topbar-right">
                  <input className="search-inp" placeholder="Search brands…" value={search} onChange={e=>setSearch(e.target.value)}/>
                  <select className="filter-sel" value={filterType} onChange={e=>setFilterType(e.target.value)}>
                    <option value="">All types</option>{COLLAB_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <select className="filter-sel" value={filterPriority} onChange={e=>setFilterPriority(e.target.value)}>
                    <option value="">All priorities</option>{PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                  <select className="filter-sel" value={filterStage} onChange={e=>setFilterStage(e.target.value)}>
                    <option value="">All stages</option>{STAGES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <button className="btn-primary" onClick={()=>setModal({})}><Plus size={14}/> Add collab</button>
                </div>
              </div>
              {view==='kanban'&&(
                <div className="kanban-scroll"><div className="kanban">
                  {STAGES.map(stage=>{
                    const cards=filtered.filter(c=>c.stage===stage.id);
                    return <div key={stage.id} className="kcol">
                      <div className="kcol-hdr"><span className="kcol-dot" style={{background:stage.color}}/><span className="kcol-label">{stage.label}</span><span className="kcol-count">{cards.length}</span></div>
                      <div className="kcol-cards">
                        {cards.map(c=><KanbanCard key={c.id} collab={c} onClick={c=>setDetail(c)}/>)}
                        {cards.length===0&&<div className="kcol-empty">—</div>}
                      </div>
                    </div>;
                  })}
                </div></div>
              )}
              {view==='list'&&(
                <div className="list-view">
                  {filtered.length===0?(<div className="empty-state"><Sparkles size={28}/><p>No collabs yet.</p><button className="btn-primary" onClick={()=>setModal({})}><Plus size={13}/> Add collab</button></div>):(
                    <table className="list-tbl">
                      <thead><tr><th>Brand</th><th>Priority</th><th>Type</th><th>Progress</th><th>Stage</th><th>Value</th><th>Due/Deadline</th><th></th></tr></thead>
                      <tbody>{filtered.map(c=>{
                        const bd=brandDeadlineInfo(c);
                        return <tr key={c.id} onClick={()=>setDetail(c)}>
                          <td><div className="list-brand"><Avatar name={c.brand} size={28}/><div><div className="list-brand-name">{c.brand}</div><div className="list-brand-cat">{c.cat}</div></div></div></td>
                          <td><PriorityBadge priority={effPriority(c)} small/></td>
                          <td><TypeBadge type={c.type} small/></td>
                          <td style={{minWidth:110}}><ProgressBar stage={c.stage} collab={c} showLabel/></td>
                          <td><StageBadge stage={c.stage} small/></td>
                          <td className="list-val">{c.value?`$${parseFloat(c.value).toLocaleString()}`:'—'}</td>
                          <td>{bd?<BrandDlBadge collab={c}/>:c.due?<DueBadge dateStr={c.due}/>:'—'}</td>
                          <td><ChevronRight size={14} className="list-arrow"/></td>
                        </tr>;
                      })}</tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )
        )}
      </main>

      {showThemePicker && (
        <div className="modal-overlay" style={{zIndex:400,alignItems:'center'}}>
          <ThemePicker current={currentTheme} onSelect={handleThemeSelect} isModal={showThemePicker === 'settings'}/>
        </div>
      )}
      {showWelcome && <WelcomePopup onClose={()=>setShowWelcome(false)}/>}
      {modal!==null&&<CollabModal collab={Object.keys(modal).length>0?modal:null} onSave={handleSave} onDelete={handleDelete} onClose={()=>setModal(null)}/>}
      {invoiceModal&&<InvoiceModal collab={invoiceModal.collab} profile={profile} existingInvoice={null} onSave={async inv=>{ await saveInvoice(user.uid,inv); setInvoiceModal(null); }} onClose={()=>setInvoiceModal(null)}/>}
      {showSettings&&<ChangePwModal onClose={()=>setShowSettings(false)}/>}
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [authState, setAuthState] = useState(null);

  useEffect(() => {
    return onAuthChange(async (firebaseUser) => {
      if (!firebaseUser) { setAuthState(false); return; }

      // Fetch profile with retry — Firestore can be slow on first load
      let profile = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        profile = await getUserProfile(firebaseUser.uid);
        if (profile) break;
        // Wait 800ms before retrying
        await new Promise(r => setTimeout(r, 800));
      }

      // Admin always approved — check email directly, never trust Firestore for admin
      const isAdminEmail = firebaseUser.email?.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase().trim();
      if (isAdminEmail) {
        // Force-create a minimal profile if Firestore returned nothing
        if (!profile) profile = { uid: firebaseUser.uid, email: firebaseUser.email, approved: true };
        profile.approved = true;
      }

      // If still no profile (new user whose doc hasn't been created yet), show pending
      if (!profile) {
        setAuthState({ user: firebaseUser, profile: { approved: false } });
        return;
      }

      if (profile?.theme) applyTheme(profile.theme);
      setAuthState({ user: firebaseUser, profile });
    });
  }, []);

  if (authState === null) return <div className="app-loading"><div className="app-spinner"/><span>Loading…</span></div>;

  return (
    <Routes>
      <Route path="/share/:shareId" element={<SharePage/>}/>
      <Route path="/:handle/links" element={<PublicLinksPage/>}/>
      <Route path="/login" element={
        authState && authState.profile?.approved
          ? <CRMApp user={authState.user} profile={authState.profile}/>
          : <AuthScreen defaultMode="login"/>
      }/>
      <Route path="/signup" element={
        authState && authState.profile?.approved
          ? <CRMApp user={authState.user} profile={authState.profile}/>
          : <AuthScreen defaultMode="register"/>
      }/>
      <Route path="*" element={
        !authState
          ? <LandingPage
              onLogin={() => window.location.href='/login'}
              onSignup={() => window.location.href='/signup'}
            />
          : !authState.profile?.approved
          ? <PendingScreen/>
          : <CRMApp user={authState.user} profile={authState.profile}/>
      }/>
    </Routes>
  );
}
